import React, { useState, useEffect, useContext, useMemo, useCallback } from 'react';
import styled from 'styled-components';
import { navigate, usePath, useQueryParams, A } from 'hooks/useHookRouterCompat';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalShipping } from '@mui/icons-material';
import { DateTime } from 'luxon';
import { Email } from '@mui/icons-material';
import * as Cons from 'data/Consignment';
import ConsignmentEntry from 'data/ConsignmentEntry';
import * as User from 'data/User';
import ShippingCarrier from 'data/ShippingCarrier';
import ManufactureModal from 'components/Consignments/Modals/ManufactureModal';
import OrderCancelModal from 'components/Consignments/Modals/OrderCancelModal';
import AdditionalShippingCostApproval from 'components/Consignments/AdditionalShippingCostApproval';
import Modal from 'components/UI/Modal';
import { Color, FontSize, FontWeight } from 'lib/styles';
import OrderFulfillmentTable from 'components/Consignments/OrderFulfillmentTable';
import Icon from 'view/Icon';
import FlexboxContainer from 'components/UI/FlexboxContainer';
import Input from 'components/UI/Input';
import Divider from 'components/UI/Divider';
import Page from 'components/UI/Page';
import LoadingIndicator from 'components/UI/LoadingIndicator';
import { renderToast, toast } from 'components/UI/ToastNotification';
import { useAPI } from 'lib/API';
import { removeAtIndex } from 'lib/Array';
import { useAuth } from 'lib/Auth';
import Breadcrumbs from 'components/UI/Breadcrumb';
import { Space } from 'styles/themeSpaces';
import { BorderRadius } from 'lib/styles';
import { LensRounded } from '@mui/icons-material';
import {
  CHECKING_CREDIT,
  ORDER_SENT_TO_OEM,
  READY,
  NEW,
  CANCELING,
  CANCELED,
  ON_HOLD,
  PROCESSING,
  MANUFACTURING,
  SPLIT_CONSIGNMENT,
  PACKING,
  SHIPPED_READY_FOR_PICKUP,
  DELIVERED_PICKED_UP,
  DELAYED,
  AWAITS_SHIPPING,
  AWAITS_SHIPPING_APPROVAL,
  SHIPPED,
  PICKED_UP,
  DOW_JONES_FAILED,
  READY_TO_SHIP,
  READY_FOR_PICKUP,
  BUYER_DELAY_CANCEL,
  BUYER_FREIGHT_COST_CANCEL,
  BUYER_FREIGHT_COST_TIMEOUT,
  RENT_IN_PROGRESS,
  RENT_RETURN_PENDING,
  RENT_COMPLETED,
  OFMP_CANCEL,
  DJ_CANCEL,
  VENDOR_REJECT,
  VENDOR_FREIGHT_COST_CANCEL,
  VENDOR_CREDIT_LIMIT_CANCEL,
  SYSTEM_CREDIT_CARD_FAILURE,
  CANCELATION_REASON_MESSAGES,
  MODIFIED_BY_CUSTOMER,
  MODIFIED_BY_VENDOR,
  RENT_UPDATED,
  HIDE_STATUS_ON_ORDER_HISTORY,
  ALLOW_SAME_STATUS_ON_ORDER_HISTORY,
  AWAITS_EDIT_APPROVAL,
  HIDE_CANCEL_CONSIGNMENT_STATES,
  BUYER_GENERAL_CANCEL,
  CANCELATION_FAILED,
  CANCELATION_APPROVAL,
  SUBSCRIPTION_ACTIVE,
  SUBSCRIPTION_RENEWAL,
  AFFILIATE_PROGRAM_ACTIVE
} from 'constants/consignmentStates';
import { DOW_JONES_BLOCKED, DOW_JONES_REVIEWED_BLOCKED } from 'constants/dowJonesStatuses';
import { SHIPPING } from 'constants/deliveryModes';
import { collapseSameStatusHistoryRuns, isWithdrawOrBuyerDecline, mapCurrentAndPreviousValuesInArray, parseConsignmentId, parseRecipientName, recalculateRentalDates, stringifyPickupContact, hasPostOrderShippingCostEntries, hasSameStockAndManufactureDate } from 'utils/consignment';
import { getSalesOrderType, shouldShowCancellationApprovalModal, shouldShowAutomaticCancellation } from 'utils/order';
import OrderCancellationEmailModal from 'components/Orders/OrderCancellationEmailModal';
import { cancellationOrderEmailRequest } from 'requests/order';
import { emulateSplitConsignment, updateConsignmentEntrySerialNumber, updateRentalState, updateSalesOrderNumber } from 'requests/consignment';
import { getDocumentsForConsignment, getDocumentsForOrder } from 'requests/documents';
import { hasDocuments } from 'utils/document';
import { loadRejectionReasonsFromStorage, formatRejectionReasons } from 'utils/rejectionReasons';
import {
  loadConsignment,
  updateDelivery,
  updateDispatch,
  updateFulfillment,
  updateState,
  updateSubscriptionState,
  checkAndCaptureCredit,
  reverseConsignment
} from 'requests/consignment';
import { Main, Text, Background, Shade, Border } from 'styles/themeColors';
import Button from 'components/UI/Button';
import ConsignmentCancelallationModal from 'components/Consignments/Modals/ConsignmentCancelationModal';
import CancellationApprovalModal from 'components/Consignments/Modals/CancellationApprovalModal';
import FeatureFlag, { FeatureFlagValues } from 'data/FeatureFlag';
import { grantAccess } from 'utils/authorization';
import ConsignmentShortPaymentsBanner from 'components/Consignments/Banners/ConsignmentShortPaymentsBanner';
import { loadShippingCarriersByCountry } from 'requests/shippingCarrier';
import ShippingDetails from 'components/Consignments/ShippingDetails';
import PickupDetails from 'components/Consignments/PickupDetails';
import Documents from 'components/Consignments/Documents';
import OrderDetailsInformation from 'components/Orders/OrderDetailsInformation';
import { capitalize, formatDateTime, getLocalTimezone } from 'utils/formatters';
import { getSteps } from 'utils/gridColumnMappings';
import Pill from 'components/UI/Pill';
import useBreadcrumbs from 'hooks/useBreadcrumbs';
import Banner from 'components/UI/Banner';
import BannerWraper from 'components/UI/BannerWraper';
import ConfirmOrder from 'components/Consignments/ConfirmOrder'
import OrderInformationTable from 'components/Orders/OrderInformationTable';
import AwaitingEditApproval from 'components/Consignments/AwaitingEditApproval';
import Select from 'components/UI/Select';
import ShippingCostRequiredItems from 'components/Consignments/ShippingCostRequiredItems';
import SplitPostOrderShippingCost from './SplitPostOrderShippingCost';
import { SocketContext } from 'context/socket';
import { getSalesOrderForConsignment, retrySalesOrderCreation, cancelSalesOrder, retryCancelSalesOrder } from 'requests/salesOrder';
import SalesOrder from 'components/Orders/SalesOrder';
import SalesOrderModal from './Modals/SalesOrderModal';
import SalesOrderDetailsModal from './Modals/SalesOrderDetailModal';
import { getCountryName } from 'utils/country';
import RentalDates from './components/RentalDates';
import { CONSIGNMENT_TYPES, TEXT_LABELS } from 'constants/order';
import RentalOrderStates from './RentalOrderStates';
import AffiliateOrderStates from './AffiliateOrderStates';
import AffiliateFormSubmitted from './AffiliateFormSubmitted';
import SubscriptionOrderStates from './SubscriptionOrderStates';
import { PAYMENT_STATUS } from '../../constants/paymentStatus';
import RentalNewDates from './components/RentalNewDates';
import { IN_STOCK, MANUFACTURE } from 'constants/consignmentEntry';
import OrderProgress from './OrderProgress';
import TicketHistory from 'components/Ticket/TicketHistory';
import OrderTag from 'components/UI/OrderTag';
import { printComponent } from 'utils/print';
import ModifyEstDateWithRentaModal from './Modals/ModifyEstDateWithRentModal';
import SplitPostOrderShippingCostRent from './SplitPostOrderShippingCostRent';
import { toDate } from 'utils/date';
import WarningBanner from 'components/UI/WarningBanner';
import OrderSnapshotPreview from 'common/OrderSnapshotPreview';
import { PermissionCode } from 'data/UserPermissions';
import { useReadPermission } from 'hooks/useUserRoles';
import { ReadOnlyWrapper } from 'components/common/ReadOnlyWrapper';
import MDMNumberSetup from './Banners/MDMNumberSetup';
import CreditCheckV2Banner from './Banners/CreditCheckV2Banner';
import StockApiErrorBanner from './StockApiErrorBanner';
import SalesOrderCancellationBanner from './SalesOrderCancellationBanner';
import SalesOrderErrorBanner from 'components/Orders/SalesOrderErrorBanner';
import { createCreditRecord, recheckCredit, updateCreditRecord, overrideCreditTransaction, getTransactionCreditByCode } from 'requests/credit';
import { assignBusinessUnitToCompany, loadCompanyDefer } from 'requests/company';
import Company from 'data/Company';
import { useBusinessUnit } from 'context/businessUnit';
import { useVendorBusinessUnitSelection } from 'hooks/useVendorBusinessUnitSelection';
import VendorSelectionDropdown from 'components/UI/VendorSelectionDropdown';
import BusinessUnitSelectionDropdown from 'components/UI/BusinessUnitSelectionDropdown';
import { CreditTransaction, TransactionCreditResponse } from 'types/credit';
import { CREDIT_SOURCES } from 'constants/credit';
import { PURCHASE_ORDER } from 'constants/paymentTypes';
import { SALES_ORDER_API_STATUSES } from 'constants/salesOrderTypes';
import { checkIsSalesOrderApplicable } from 'utils/salesorder';
import { useUserRole } from 'hooks/useUserRole';
import Vendor from 'data/Vendor';
import { BusinessUnit } from 'data/BusinessUnit';
import { EDIT_ORDER_RESULTS } from 'constants/product';

const StyledLocalShipping = styled(LocalShipping) <any>`
  font-size: 44px !important;
  color: ${Text.primary} !important;
  margin: 0 16px;
`;

const StyledWarehouseIcon = styled(Icon).attrs({ name: 'Warehouse', size: 44 })`
  color: ${Text.primary} !important;
  margin: 0 16px !important;
`;

const LensRoundedIcon = styled(LensRounded) <any>`
  font-size: 10px !important;
  margin-left: 5px;
`;

const PlacedDate = styled.span`
  margin: 12px 0 32px 16px;
  font-size: 14px;
  font-style: italic;
  line-height: 18px;
`;

const StyledSlashIcon = styled(Icon).attrs({ name: 'Slash', size: 16 })`
  color: ${Text.primary};
`;

const SubHeading = styled.div`
  font-size: ${FontSize.medium};
  font-weight: ${FontWeight.bold};
  margin-bottom: 10px;
`;

const Content = styled.div`
  flex: 80%;
  padding: 0 10px;
  margin-bottom: 20px;
`;

const Section = styled.div`
  margin-top: ${Space.sp3x};
`;

const LabelText = styled.span`
  display: block;
  font-weight: ${FontWeight.medium};
  text-align: left;
  color: ${Text.primary};
  font-size: 16px;
  line-height: 20px;
  margin-bottom: 8px;
`;

const DisabledField = styled.div`
  background-color: #E9ECEF;
  border-radius: 6px;
  padding: 8px;
  height: 36px;
  line-height: 20px;
  font-size: 16px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

const DateFieldContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 259px;
  flex-shrink: 0;
`;

const WarningMessage = styled.span`
  padding: 16px 0;
  font-style: normal;
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;
`;

const Message = styled.span`
  font-style: normal;
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;
  margin-left: 10px;
`;

const Label = styled.span`
  font-weight: 400;
  line-height: 24px;
  color: ${Text.primary};
  font-size: 16px;
  padding: 16px;
`;

const Span = styled.span`
  margin: ${Space.spBase} 0;
`;

const WhiteLoadingIndicator = styled(LoadingIndicator)`
  filter: brightness(0) invert(1);
`;

const Link = styled(A) <any>`
  color: ${Text.primary};

  &:hover {
    text-decoration: none;
  }
`;

const BreadcrumbContainer = styled(FlexboxContainer)`
  cursor: pointer;
  margin-left: ${Space.spHalf};
  margin-right: ${Space.spHalf};
  border-radius: ${BorderRadius.medium};

  &:hover {
    background: rgba(123, 97, 255, 0.05);
  }
`;

const PrintIcon = styled(Icon).attrs({ name: 'Print', size: 30 })``;

interface Props {
  consignmentId: string;
  featureFlags: FeatureFlag;
  updateVendor: (name: string, brandId?: number) => void;
  selectedVendor?: Vendor;
  selectedBusinessUnit?: BusinessUnit;
  vendorsList?: Vendor[];
};

const OrderDetails = ({ consignmentId, featureFlags, updateVendor, selectedVendor, selectedBusinessUnit, vendorsList = [] }: Props) => {
  const { user } = useAuth();
  const { businessUnitList } = useBusinessUnit();
  const path = usePath();
  const crumbs = useBreadcrumbs(path);
  const [queryParams, setQueryParams] = useQueryParams();
  const country = queryParams.country as string;
  const [previousUrl, setPreviousUrl] = useState('');
  const [consignmentDetailsResponse, setConsignmentDetailsResponse] = useState<Cons.ConsignmentDetails>(Cons.emptyConsignmentDetails);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isModalOnHoldOpen, setIsModalOnHoldOpen] = useState(false);
  const [isConfirmFulfillmentModalOpen, setIsConfirmFulfillmentModalOpen] = useState(false);
  const [salesOrderModalOpen, setSalesOrderModalOpen] = useState(false);
  const [salesOrderDetailsModalOpen, setSalesOrderDetailsModalOpen] = useState(false);
  const [salesOrderLoading, setSalesOrderLoading] = useState(false);
  const [salesOrderError, setSalesOrderError] = useState(false);
  const [salesOrderTimeout, setSalesOrderTimeout] = useState(false);
  const [salesOrderModalTimeout, setSalesOrderModalTimeout] = useState(false);
  const [isDispatchModalOpen, setIsDispatchModalOpen] = useState(false);
  const [isManufactureModalOpen, setIsManufactureModalOpen] = useState(false);
  const [isPickupModalOpen, setIsPickupModalOpen] = useState(false);
  const [isDelayModalOpen, setIsDelayModalOpen] = useState(false);
  const [isDelayedAcceptModalOpen, setIsDelayedAcceptModalOpen] = useState(false);
  const [isConfirmOrderModalOpen, setIsConfirmOrderModalOpen] = useState(false);
  const [shouldRedirect, setShouldRedirect] = useState(false);
  const [shippingCarriers, setShippingCarriers] = useState<ShippingCarrier[]>([]);
  const [isFulfillmentConfirmModalOpen, setIsFulfillmentConfirmModalOpen] = useState(false);
  const [openEstDeliveryPicker, setOpenEstDeliveryPicker] = useState(false);
  const [openPickupDatePicker, setOpenPickupDatePicker] = useState(false);
  const [openDispatchDatePicker, setOpenDispatchDatePicker] = useState(false);
  const [shippingConsignmentRequest, setShippingConsignmentRequest] = useState<Cons.ShippingConsignmentRequest>({
    shippingCarrier: {
      id: 0,
      name: ''
    },
    trackingId: '',
    deliveryDate: '',
    attachDocuments: false,
    rentalNewStartDate: '',
    rentalNewEndDate: ''
  });
  const isRental = consignmentDetailsResponse.consignmentType === CONSIGNMENT_TYPES.RENT;
  const isSubscription = consignmentDetailsResponse.consignmentType === CONSIGNMENT_TYPES.SUBSCRIPTION;
  const isAffiliateProgram = consignmentDetailsResponse.consignmentType === CONSIGNMENT_TYPES.AFFILIATE_PROGRAM;
  const isSubscriptionProcessingUnpaid = isSubscription && consignmentDetailsResponse.state === PROCESSING && consignmentDetailsResponse.order.paymentType !== 'PURCHASE_ORDER' && consignmentDetailsResponse.paymentStatus === PAYMENT_STATUS.NOT_PAID;
  const isAffiliateProcessingUnpaid = isAffiliateProgram && consignmentDetailsResponse.state === PROCESSING && consignmentDetailsResponse.order.paymentType !== 'PURCHASE_ORDER' && consignmentDetailsResponse.paymentStatus === PAYMENT_STATUS.NOT_PAID;
  // Edit order is available for purchase orders in New status, behind the editOrder
  // feature flag — clicking it navigates to the edit order route; the consignment
  // state stays ORDER_SENT_TO_OEM for the whole edit session (no state transition)
  const canEditOrder = consignmentDetailsResponse.consignmentType === CONSIGNMENT_TYPES.PURCHASE &&
    consignmentDetailsResponse.state === ORDER_SENT_TO_OEM &&
    grantAccess(featureFlags, FeatureFlagValues.EDIT_ORDER, user!);
  // After an edit session ends (withdraw, decline) the order is New in SellerHub before DS has
  // caught up; DS confirms with ORDER_SENT_TO_OEM in both states. Acting before that reply
  // lands races it - a late reply overwrote a fresh send for approval (6873158) - so the New
  // actions stay blocked until both states read ORDER_SENT_TO_OEM.
  const hasOrderChanges = consignmentDetailsResponse.consignmentHistoryChanges?.some(x => x.toConsignmentStatus === AWAITS_EDIT_APPROVAL);
  const isOrderSyncPending = canEditOrder && hasOrderChanges &&
    consignmentDetailsResponse.previousState !== ORDER_SENT_TO_OEM && consignmentDetailsResponse.state === ORDER_SENT_TO_OEM;
  // Applied edit-order changes were sent to the buyer — the order stays in this state
  // until the buyer responds, and OrderDetails shows the awaiting-approval view
  const isAwaitingEditApproval = consignmentDetailsResponse?.state === AWAITS_EDIT_APPROVAL &&
    consignmentDetailsResponse?.consignmentType === CONSIGNMENT_TYPES.PURCHASE;
  // Changes are applied and the buyer's approval is still required — the seller cannot
  // return to the order details flow until the buyer responds
  const isApprovalRequestPending = isAwaitingEditApproval &&
    consignmentDetailsResponse.editOrderResult === EDIT_ORDER_RESULTS.SUCCESS;
  const [pickupConsignmentRequest, setPickupConsignmentRequest] = useState<Cons.PickupConsignmentRequest>({
    warehouseId: 0,
    referenceNumber: '',
    pickupDate: '',
    attachDocuments: false
  });
  const [dispatchRequest, setDispatchRequest] = useState<Cons.DispatchRequest>({
    dispatchDate: '',
    pickedUpBy: ''
  });
  const [fulfillmentRequest, setFulfillmentRequest] = useState<ConsignmentEntry[]>([]);
  const [isPreparationStep, setIsPreparationStep] = useState(false);
  const [isFulfillmentStep, setIsFulfillmentStep] = useState(false);
  const [isAwaitingDispatchStep, setIsAwaitingDispatchStep] = useState(false);
  const [isRentalStep, setIsRentalStep] = useState(false);
  const [isFulfillmentCompleteStep, setIsFulfillmentCompleteStep] = useState(false);
  const [isDelayedStep, setIsDelayedStep] = useState(false);
  const [isShipping, setIsShipping] = useState(false);
  const [cancellationEmailPopup, setCancellationEmailPopup] = useState(false);
  const isVendor = User.hasOfmpAdminProfile(user) || User.isOfmpAdmin(user!) ? false : true;
  const [cancellationConsignmentPopup, setCancellationConsignmentPopup] = useState(false);
  const [cancellationApprovalPopup, setCancellationApprovalPopup] = useState(false);
  const [isAutomaticCancellationMode, setIsAutomaticCancellationMode] = useState(false);
  const [documents, setDocuments] = useState<any>({});
  const [salesOrderResult, setSalesOrderResult] = useState<any>({});
  const [orderDocuments, setOrderDocuments] = useState<any>({});
  const [openUpdateOrderModel, setOpenUpdateOrderModal] = useState(false);
  const [checkboxValue, setCheckboxValue] = useState(false);
  const [openOptionalInfoSection, setOpenOptionalInfoSection] = useState(false);
  const [errorMessage, setErrorMessage] = useState('')
  const [rejectionReasons, setRejectionReasons] = useState<Array<{ id: string; value: string }>>([]);
  const [isInvalid, setIsInvalid] = useState(false);
  const [isDeclineModalOpen, setIsDeclineModalOpen] = useState(false);
  const [isPostOrderShippingCostSplitFormOpen, setIsPostOrderShippingCostSplitFormOpen] = useState(false);
  const [splitConsignments, setSplitConsignments] = useState<any>([]);
  // Subscription-specific state
  const [subscriptionCompany, setSubscriptionCompany] = useState<Company | null>(null);
  const [subscriptionVendor, setSubscriptionVendor] = useState<Vendor | null>(null);
  const [subscriptionBusinessUnit, setSubscriptionBusinessUnit] = useState<BusinessUnit | null>(null);
  const [liveUpdates, setLiveUpdates] = useState(false);
  const [additionalShippingCostsValues, setAdditionalShippingCostsValues] = useState<Cons.SplitConsignmentShippingCostRequest[]>([]);
  const [splitConsignmentBody, setSplitConsignmentBody] = useState<any>({});
  const [originalDeliveryDate, setOriginalDeliveryDate] = useState<string>();
  const [originalReferenceNumber, setOriginalReferenceNumber] = useState<string>();
  const [rentalStartDate, setRentalStartDate] = useState<string | null>(null);
  const [rentalEndDate, setRentalEndDate] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const [loader, setLoader] = useState(false);
  const [isDisabled, setIsDisabled] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const socket = useContext(SocketContext);
  const [isReviewPage, setIsReviewPage] = useState(false);
  const [isReviewPageMounted, setIsReviewPageMounted] = useState(false);
  const [hiddenStates, setHiddenStates] = useState(HIDE_CANCEL_CONSIGNMENT_STATES);
  const [serialNumbers, setSerialNumbers] = useState<any[]>([]);
  const [enableAutomatedCreditCheck, setEnableAutomatedCreditCheck] = useState(false);
  const [mdmNumber, setMdmNumber] = useState('');
  const [mdmErrorMessage, setMdmErrorMessage] = useState('');
  const [showAutomatedCC, setShowAutomatedCC] = useState(true);
  const [isManualConfirmed, setIsManualConfirmed] = useState(false);

  // Stock API Error Banner state - show if any stocksApiValidation is false
  const showStockApiError = useMemo(() => {
    return consignmentDetailsResponse?.consignmentEntries?.some(entry => entry.stocksApiValidation === false) ?? false;
  }, [consignmentDetailsResponse?.consignmentEntries]);

  // Check if any fulfillment entry requires post-order shipping cost
  const hasPostOrderShippingCost = useMemo(() => {
    return fulfillmentRequest.some(entry => entry.postOrderShippingCost === true && entry.deliveryMode === SHIPPING);
  }, [fulfillmentRequest]);

  // Permissions
  const { readOnly: isShippingDispatchReadOnly } = useReadPermission(PermissionCode.READY_DISPATCH_DATE);
  const { readOnly: isPickupDispatchReadOnly } = useReadPermission(PermissionCode.PICKED_UP_DETAILS_MANAGEMENT);
  const { readOnly: additionalShippingCostReadOnly } = useReadPermission(PermissionCode.ADDITIONAL_SHIPPING_COSTS);

  const userRole = useUserRole({ user, selectedVendor, selectedBusinessUnit }).userRole;

  // Override Credit
  const handleConsignmentOnHoldtoProcessV2 = () => {
    const creditId = consignmentDetailsResponse.businessUnitCompany?.creditId;

    if (!creditId) {
      renderToast(toast.TYPE.ERROR, 'Credit ID not found. Cannot proceed with credit override.');
      return;
    }

    overrideCreditTransactionReq.run(consignmentId, creditId);
  };

  const handleRecheckCredit = () => {
    recheckCreditReq.run(consignmentId, consignmentDetailsResponse.businessUnitCompany?.creditId);
  }

  const handleRetrySalesOrderCreation = () => {
    retrySalesOrderReq.run(consignmentId);
  }

  const handleRetryCancellation = () => {
    retryCancelSalesOrderReq.run(consignmentId);
  };

  const handleApproveCancellation = () => {
    // Check if all sales orders have orderRejectionStatus as "Completely Processed"
    const COMPLETELY_PROCESSED = 'Completely Processed';

    const allCompletelyProcessed = Array.isArray(salesOrderResult) &&
      salesOrderResult.length > 0 &&
      salesOrderResult.every(order => order.orderRejectionStatus === COMPLETELY_PROCESSED);

    if (!allCompletelyProcessed) {
      renderToast(toast.TYPE.ERROR, 'Rejection for sales order is not completely processed.', { autoClose: 5000 });
      return;
    }

    updateStateReq.run(consignmentId, CANCELING, null, userRole);
  };

  const handleRejectCancellation = () => {
    reverseConsignmentReq.run(consignmentId);
  };

  const handleShowCancellationEmailPopup = () => setCancellationEmailPopup(true);
  const handleCloseCancellationEmailPopup = () => setCancellationEmailPopup(false);
  const handleShowCancellationConsignmentPopup = () => {
    // Check if we should show cancellation approval modal for PURCHASE orders with sales orders
    const shouldShowApprovalModal = shouldShowCancellationApprovalModal(consignmentDetailsResponse, featureFlags);

    if (shouldShowApprovalModal) {
      const isAutomaticCancellation = shouldShowAutomaticCancellation(salesOrderResult);
      setIsAutomaticCancellationMode(isAutomaticCancellation);
      setCancellationApprovalPopup(true);
    } else {
      setCancellationConsignmentPopup(true);
    }
  };
  const handleCloseCancellationConsignmentPopup = () => setCancellationConsignmentPopup(false);
  const handleCloseCancellationApprovalPopup = () => setCancellationApprovalPopup(false);

  // Stock API Error Banner handlers
  const handleRetryStockApi = () => {
    refreshConsignment();
  };

  const handleManualStockConfirm = (confirmed: boolean) => {
    setIsManualConfirmed(confirmed);
  };

  const showConsignmentShortPaymentsBanner = Boolean(
    consignmentDetailsResponse.order.paymentType === 'PURCHASE_ORDER' &&
    consignmentDetailsResponse.businessUnitCompanyId &&
    (consignmentDetailsResponse.state === DELIVERED_PICKED_UP || consignmentDetailsResponse.state === SHIPPED || consignmentDetailsResponse.state === PICKED_UP)
  );


  const handleConsignmentCancellationConfirm = (consignmentCancelingReason: string) => {
    updateStateReq.run(consignmentId, CANCELING, consignmentCancelingReason, userRole);
  };

  const handleCancellationApprovalConfirm = (selectedOption: { id: string; value: string }) => {
    cancelSalesOrderReq.run(consignmentId, selectedOption.value, userRole, selectedOption.id, isAutomaticCancellationMode);
  };

  // Right after an approval on the buyer's behalf, Hybris has yet to renumber the entries, and
  // a split confirmed now posts entries that are about to be re-created under new ids
  const isIncorporatingEditChanges = !!consignmentDetailsResponse.isIncorporatingEditChanges;

  const isOrderFulfillmentButtonDisabled = isIncorporatingEditChanges || fulfillmentRequest.some(
    (consignmentEntry: ConsignmentEntry) => {
      return (
        consignmentEntry.radioButtonValue &&
        (
          (consignmentEntry.radioButtonValue === IN_STOCK &&
            !consignmentEntry.isDeleted &&
            ((!consignmentEntry.committedDate && !consignmentEntry.preliminaryDeliveryDate) ||
              consignmentEntry.committedQuantity < 1 ||
              (consignmentEntry.rentalStartDate && consignmentEntry.rentalEndDate && consignmentEntry.rentalStartDate > consignmentEntry.rentalEndDate)
            )
          ) ||
          (consignmentEntry.radioButtonValue === MANUFACTURE &&
            !consignmentEntry.isDeleted &&
            ((!consignmentEntry.manufactureDate && !consignmentEntry.preliminaryDeliveryDate) ||
              consignmentEntry.manufactureQuantity < 1 ||
              (consignmentEntry.rentalStartDate && consignmentEntry.rentalEndDate && consignmentEntry.rentalStartDate > consignmentEntry.rentalEndDate)
            )
          )
        )
      );
    }
  );

  const isDeliveryButtonDisabled =
    !shippingConsignmentRequest.shippingCarrier.name ||
    !shippingConsignmentRequest.trackingId ||
    !shippingConsignmentRequest.deliveryDate;

  const salesOrderInProgressStatuses: string[] = [SALES_ORDER_API_STATUSES.IN_PROGRESS, SALES_ORDER_API_STATUSES.RETRY_INITIATED];

  const isSalesOrderInProgress = checkIsSalesOrderApplicable(consignmentDetailsResponse) &&
    (!consignmentDetailsResponse.salesOrderStatus ||
      salesOrderInProgressStatuses.includes(consignmentDetailsResponse.salesOrderStatus?.status));

  const isPickupButtonDisabled =
    !pickupConsignmentRequest.referenceNumber ||
    !pickupConsignmentRequest.pickupDate;

  const navigateBack = previousUrl
    ? previousUrl
    : `/orders/page/${queryParams.ordersPage}`;

  const navigateBackQueryParams = previousUrl
    ? { activeTab: 1, ordersPage: parseInt(queryParams.ordersPage) }
    : {};

  const renderCancelingMessage = (cancelationReason: string) => {
    switch (cancelationReason) {
      case OFMP_CANCEL:
        return CANCELATION_REASON_MESSAGES.OFMP_CANCEL;
      case DJ_CANCEL:
        return CANCELATION_REASON_MESSAGES.DJ_CANCEL;
      case VENDOR_REJECT:
        // Affiliate orders share the VENDOR_REJECT code but display a different title
        return isAffiliateProgram
          ? CANCELATION_REASON_MESSAGES.VENDOR_REJECT_AFFILIATE
          : CANCELATION_REASON_MESSAGES.VENDOR_REJECT;
      case VENDOR_FREIGHT_COST_CANCEL:
        return CANCELATION_REASON_MESSAGES.VENDOR_FREIGHT_COST_CANCEL;
      case VENDOR_CREDIT_LIMIT_CANCEL:
        return CANCELATION_REASON_MESSAGES.VENDOR_CREDIT_LIMIT_CANCEL;
      case SYSTEM_CREDIT_CARD_FAILURE:
        return CANCELATION_REASON_MESSAGES.SYSTEM_CREDIT_CARD_FAILURE;
      case BUYER_DELAY_CANCEL:
        return CANCELATION_REASON_MESSAGES.BUYER_DELAY_CANCEL;
      case BUYER_FREIGHT_COST_TIMEOUT:
        return CANCELATION_REASON_MESSAGES.BUYER_FREIGHT_COST_TIMEOUT;
      case BUYER_FREIGHT_COST_CANCEL:
        return CANCELATION_REASON_MESSAGES.BUYER_FREIGHT_COST_CANCEL;
      case BUYER_GENERAL_CANCEL:
        return CANCELATION_REASON_MESSAGES.BUYER_GENERAL_CANCEL;
      default:
        return CANCELATION_REASON_MESSAGES.VENDOR_REJECT;
    }
  };

  const renderCancelingText = (cancelationReason: any) => {
    if (!cancelationReason) return '';
    if ([OFMP_CANCEL, VENDOR_REJECT, BUYER_GENERAL_CANCEL].includes(cancelationReason.reasonCode)) {

      if (cancelationReason?.cancelationMessage) {
        return `Cancelation reason: ${cancelationReason?.cancelationMessage}`
      }
      else {
        return ``;
      }

    }
  };

  const onDeclineClick = () => {
    setIsModalOpen(true);
  };



  const validateMdmNumber = () => {
    if (enableAutomatedCreditCheck && !mdmNumber) {
      setMdmErrorMessage(TEXT_LABELS.PLEASE_PROVIDE_MDM_NUMBER);
      return false;
    } else if (mdmNumber && showAutomatedCC) {
      setMdmErrorMessage(TEXT_LABELS.VERIFY_MDM_NUMBER);
      return false;
    }
    return true;
  }

  const onAcceptClick = (state: string) => {
    if (!validateMdmNumber()) return;

    updateStateReq.run(consignmentId, state);
    setShouldRedirect(false);
  };

  const cancelConfirmFulfillmentModal = () => {
    setIsConfirmFulfillmentModalOpen(false);
  }

  const onModalClose = () => {
    setIsModalOpen(false);
    setIsFulfillmentConfirmModalOpen(false);
    setIsConfirmFulfillmentModalOpen(false);
    setIsDeclineModalOpen(false);
    setIsModalOnHoldOpen(false);
    setIsDispatchModalOpen(false);
    setIsManufactureModalOpen(false);
    setIsPickupModalOpen(false);
    setIsDelayModalOpen(false);
    setIsDelayedAcceptModalOpen(false);
    setIsConfirmOrderModalOpen(false);
    setIsPostOrderShippingCostSplitFormOpen(false);
    setSalesOrderModalOpen(false);
    setSalesOrderDetailsModalOpen(false);
    setCancellationApprovalPopup(false);
    onDeliveryDateChange(originalDeliveryDate);
    onPickupDateChange(originalDeliveryDate);
  };

  const onUpdatePreparationClick = (attachDocuments: boolean, isConfirmed: boolean = true) => {
    setIsConfirmOrderModalOpen(false);
    const requestBody = isShipping
      ? { ...shippingConsignmentRequest, attachDocuments }
      : { ...pickupConsignmentRequest, attachDocuments };

    if (isShipping) {
      setShippingConsignmentRequest({ ...shippingConsignmentRequest, attachDocuments });
    } else {
      setPickupConsignmentRequest({ ...pickupConsignmentRequest, attachDocuments });
    }

    if (
      (consignmentDetailsResponse.availableDate && isShipping && shippingConsignmentRequest.deliveryDate && new Date(shippingConsignmentRequest.deliveryDate).setUTCHours(0, 0, 0, 0) > new Date(consignmentDetailsResponse.availableDate).setUTCHours(0, 0, 0, 0)) ||
      (consignmentDetailsResponse.availableDate && !isShipping && pickupConsignmentRequest.pickupDate && new Date(pickupConsignmentRequest.pickupDate).setUTCHours(0, 0, 0, 0) > new Date(consignmentDetailsResponse.availableDate).setUTCHours(0, 0, 0, 0))
    ) {
      setIsDelayModalOpen(true);
    } else {
      deliveryUpdateReq.run(consignmentId, requestBody, isConfirmed);
    }
  };

  const onConfirmFulfillmentModal = () => {
    if (hasPostOrderShippingCost) {
      onModalClose();
      setIsReviewPage(false);
      setIsReviewPageMounted(false);

      // Build fulfillmentData from splitConsignmentBody for state update
      const fulfillmentData = {
        consignmentEntries: splitConsignmentBody.entries
          .map((splitEntry: any) => {
            // Find non-deleted entry first, fallback to deleted entry for cancel-only entries
            const originalEntry = fulfillmentRequest.find(e => e.entryNumber === splitEntry.entryNumber && !e.isDeleted)
              || fulfillmentRequest.find(e => e.entryNumber === splitEntry.entryNumber);
            return {
              id: originalEntry?.id,
              committedQuantity: splitEntry.committedQuantity || 0,
              committedDate: splitEntry.committedDate || 0,
              manufactureQuantity: splitEntry.manufactureQuantity || 0,
              manufactureDate: splitEntry.manufactureDate || 0,
              cancelQuantity: splitEntry.cancelQuantity || 0,
              committedEndDate: splitEntry.committedEndDate || 0,
              manufactureEndDate: splitEntry.manufactureEndDate || 0,
              rentalStartDate: splitEntry.rentalStartDate || 0,
              rentalEndDate: splitEntry.rentalEndDate || 0
            };
          })
      };

      // Update state to AWAITS_SHIPPING - fulfillmentData saves user selections to server
      // useEffect will then fetch updated data and call splitConsignmentReq
      updateStateReq.run(consignmentId, AWAITS_SHIPPING, null, null, [], null, null, fulfillmentData);
    } else {
      onModalClose();
      setIsConfirmFulfillmentModalOpen(true);
    }
  }

  const fulfillmentModal = (
    <Modal
      showModal={isFulfillmentConfirmModalOpen}
      onClose={onModalClose}
      onGhostButtonClick={onModalClose}
      onClick={onConfirmFulfillmentModal}
      title="Estimated dates may be set wrong"
      primaryButtonText="Yes, proceed with the split"
      ghostButtonText="Cancel"
      theme='error'
    >
      <FlexboxContainer flexDirection='column' width='100%'>
        <WarningMessage>You have set the same estimated date for:</WarningMessage>
        <Message><LensRoundedIcon /> items which are in stock</Message>
        <Message><LensRoundedIcon /> and items which yet to be manufactured.</Message>
        <WarningMessage>Are you sure all dates are correct?</WarningMessage>
      </FlexboxContainer>
    </Modal>
  );



  const onConfirmFulfillmentClick = () => {
    const allConsignmentsAreDeleted = fulfillmentRequest.every((consignment) => consignment.isDeleted);

    if (fulfillmentRequest.length > 1 && !allConsignmentsAreDeleted) {
      if (hasPostOrderShippingCost) {
        setIsConfirmFulfillmentModalOpen(true);
        setIsInvalid(false);
      } else {
        if (hasSameStockAndManufactureDate(fulfillmentRequest)) {
          setIsFulfillmentConfirmModalOpen(true);
        } else {
          setIsConfirmFulfillmentModalOpen(true);
        }
      }
    } else {
      setIsConfirmFulfillmentModalOpen(true);
    }
  };

  const onConfirmDispatchClick = () => {
    if (isShipping) {
      setIsDispatchModalOpen(true);
    } else {
      setIsPickupModalOpen(true);
    }
  };

  const onConfirmManufacturingClick = () => {
    setIsManufactureModalOpen(true);
  };

  const onAcceptDelayClick = () => {
    setIsDelayedAcceptModalOpen(true);
  };

  const onViewPaymentStatus = () => {
    // TODO - go to payments page!!!
  };

  const onConfirmDeclineModalClick = (value: string) => {
    updateStateReq.run(consignmentId, CANCELING, value, userRole);
    setShouldRedirect(false);
    onModalClose();
  };

  const onConfirmCancelModalClick = (value: string, reasonType?: string) => {
    if (consignmentDetailsResponse.salesOrderNumber) {
      cancelSalesOrderReq.run();
    } else {
      updateStateReq.run(consignmentId, CANCELING, value, userRole, null, reasonType);
      setShouldRedirect(false);
      onModalClose();
    }

  };

  const handleOnHoldBannerDeclineConfirmation = (value: string) => {
    onConfirmCancelModalClick(value, VENDOR_CREDIT_LIMIT_CANCEL);
  };

  const onContinueClick = (isConfirmed: boolean = true) => {
    if (hasDocuments(documents) && consignmentDetailsResponse.state === PACKING) {
      setIsConfirmOrderModalOpen(true);
    } else {
      const confirmed = typeof isConfirmed === 'boolean' ? isConfirmed : true;
      onUpdatePreparationClick(false, confirmed);
    }
  };

  const handleSerialNumberUpdate = (consignmentEntryId: number, serialNumber: string) => {
    updateConsignmentEntrySerialNumberReq.run(consignmentDetailsResponse.consignmentId!, consignmentEntryId, serialNumber);
    setConsignmentDetailsResponse((prevState) => ({
      ...prevState,
      consignmentEntries: prevState.consignmentEntries.map((entry) => {
        if (entry.id === consignmentEntryId) {
          return { ...entry, serialNumber };
        }
        return entry;
      })
    }));
  }

  const onConfirmFulfillmentModalClick = () => {
    // Check if manual stock confirmation is required
    if (showStockApiError && !isManualConfirmed) {
      renderToast(toast.TYPE.ERROR, TEXT_LABELS.STOCK_INSUFFICIENT_MANUAL_ERROR);
      return;
    }

    let newConsignmentEntries: ConsignmentEntry[] = fulfillmentRequest;

    for (let i = 0; i < newConsignmentEntries.length; i++) {
      const foundDeletedIndex = newConsignmentEntries.findIndex((entry, index) => index !== i && entry.id === newConsignmentEntries[i].id && entry.isDeleted);
      const foundEntryIndex = newConsignmentEntries.findIndex((entry, index) => index !== i && entry.id === newConsignmentEntries[i].id && !entry.isDeleted);

      if (foundDeletedIndex !== -1) {
        newConsignmentEntries[i].cancelQuantity = newConsignmentEntries[foundDeletedIndex].cancelQuantity;
        newConsignmentEntries[i].cancellationReason = newConsignmentEntries[foundDeletedIndex].cancellationReason;
        newConsignmentEntries = removeAtIndex(newConsignmentEntries, foundDeletedIndex);
      }

      // If items are split, we need to update the committed and manufacture dates for purchase or rent respectively
      if (foundEntryIndex !== -1) {

        if (consignmentDetailsResponse.consignmentType === CONSIGNMENT_TYPES.RENT) {
          newConsignmentEntries[i].committedQuantity = newConsignmentEntries[foundEntryIndex].committedQuantity > 0 ? newConsignmentEntries[foundEntryIndex].committedQuantity : newConsignmentEntries[i].committedQuantity;
          newConsignmentEntries[i].manufactureQuantity = newConsignmentEntries[foundEntryIndex].manufactureQuantity > 0 ? newConsignmentEntries[foundEntryIndex].manufactureQuantity : newConsignmentEntries[i].manufactureQuantity;
          newConsignmentEntries[i].cancellationReason = newConsignmentEntries[foundEntryIndex].cancellationReason ? newConsignmentEntries[foundEntryIndex].cancellationReason : newConsignmentEntries[i].cancellationReason;
          newConsignmentEntries[i].committedDate = newConsignmentEntries[foundEntryIndex].committedQuantity > 0 ? newConsignmentEntries[foundEntryIndex].rentalStartDate : newConsignmentEntries[i].rentalStartDate;
          newConsignmentEntries[i].committedEndDate = newConsignmentEntries[foundEntryIndex].committedQuantity > 0 ? newConsignmentEntries[foundEntryIndex].rentalEndDate : newConsignmentEntries[i].rentalEndDate;
          newConsignmentEntries[i].manufactureDate = newConsignmentEntries[foundEntryIndex].manufactureQuantity > 0 ? newConsignmentEntries[foundEntryIndex].rentalStartDate : newConsignmentEntries[i].rentalStartDate;
          newConsignmentEntries[i].manufactureEndDate = newConsignmentEntries[foundEntryIndex].manufactureQuantity > 0 ? newConsignmentEntries[foundEntryIndex].rentalEndDate : newConsignmentEntries[i].rentalEndDate;
        }

        if (consignmentDetailsResponse.consignmentType === CONSIGNMENT_TYPES.PURCHASE) {
          newConsignmentEntries[i].committedQuantity = newConsignmentEntries[foundEntryIndex].committedQuantity > 0 ? newConsignmentEntries[foundEntryIndex].committedQuantity : newConsignmentEntries[i].committedQuantity;
          newConsignmentEntries[i].committedDate = newConsignmentEntries[foundEntryIndex].committedQuantity > 0 ? newConsignmentEntries[foundEntryIndex].committedDate : newConsignmentEntries[i].committedDate;
          newConsignmentEntries[i].manufactureQuantity = newConsignmentEntries[foundEntryIndex].manufactureQuantity > 0 ? newConsignmentEntries[foundEntryIndex].manufactureQuantity : newConsignmentEntries[i].manufactureQuantity;
          newConsignmentEntries[i].manufactureDate = newConsignmentEntries[foundEntryIndex].manufactureQuantity > 0 ? newConsignmentEntries[foundEntryIndex].manufactureDate : newConsignmentEntries[i].manufactureDate;
          newConsignmentEntries[i].cancellationReason = newConsignmentEntries[foundEntryIndex].cancellationReason ? newConsignmentEntries[foundEntryIndex].cancellationReason : newConsignmentEntries[i].cancellationReason;
        }

        newConsignmentEntries = removeAtIndex(newConsignmentEntries, foundEntryIndex);
      }

      if (foundEntryIndex === -1) {

        if (consignmentDetailsResponse.consignmentType === CONSIGNMENT_TYPES.PURCHASE) {
          newConsignmentEntries[i].committedDate = newConsignmentEntries[i].committedQuantity > 0 ? newConsignmentEntries[i].committedDate : '';
          newConsignmentEntries[i].manufactureDate = newConsignmentEntries[i].manufactureQuantity > 0 ? newConsignmentEntries[i].manufactureDate : '';
        }

        if (consignmentDetailsResponse.consignmentType === CONSIGNMENT_TYPES.RENT) {
          newConsignmentEntries[i].rentalStartDate = newConsignmentEntries[i].rentalStartDate || '';
          newConsignmentEntries[i].rentalEndDate = newConsignmentEntries[i].rentalEndDate || '';
          newConsignmentEntries[i].committedDate = newConsignmentEntries[i].committedQuantity > 0 ? newConsignmentEntries[i].rentalStartDate : '';
          newConsignmentEntries[i].committedEndDate = newConsignmentEntries[i].committedQuantity > 0 ? newConsignmentEntries[i].rentalEndDate : '';
          newConsignmentEntries[i].manufactureDate = newConsignmentEntries[i].manufactureQuantity > 0 ? newConsignmentEntries[i].rentalStartDate : '';
          newConsignmentEntries[i].manufactureEndDate = newConsignmentEntries[i].manufactureQuantity > 0 ? newConsignmentEntries[i].rentalEndDate : '';
        }
      }
    }


    if (consignmentDetailsResponse.consignmentType === CONSIGNMENT_TYPES.RENT && additionalShippingCostsValues.length > 0) {
      additionalShippingCostsValues.forEach((asp) => {
        const findEntry = newConsignmentEntries?.find((entry) => asp?.entryNumbers?.includes(entry.entryNumber));
        if (findEntry) {
          asp.rentalStartDate = asp?.state === MANUFACTURING ? new Date(findEntry.manufactureDate).setUTCHours(0, 0, 0, 0) : new Date(findEntry.committedDate).setUTCHours(0, 0, 0, 0);
          asp.rentalEndDate = asp?.state === MANUFACTURING ? new Date(findEntry.manufactureEndDate!).setUTCHours(0, 0, 0, 0) : new Date(findEntry.committedEndDate!).setUTCHours(0, 0, 0, 0);
        }

        delete asp.entryNumbers;
      })
    }


    // Filter out disabled shipping cost entries
    const filteredAdditionalShippingCosts = additionalShippingCostsValues.filter((shippingCost: any) => {
      const consignment = splitConsignments.find((cons: any) => cons.consignmentId === shippingCost.consignmentId);
      if (!consignment) return false;
      return hasPostOrderShippingCostEntries(consignment.entries, fulfillmentRequest);
    });

    const consignmentEntriesWithAdditionalShippingValues = [newConsignmentEntries, filteredAdditionalShippingCosts];

    const payload = {
      consignment: consignmentEntriesWithAdditionalShippingValues,
      consignmentType: consignmentDetailsResponse.consignmentType,
      materialStocksValidation: consignmentDetailsResponse.consignmentEntries?.map(entry => ({
        entryId: entry.code,
        materialApiValidation: entry.materialApiValidation,
        stocksApiValidation: entry.stocksApiValidation,
      })),
      isStockManual: isManualConfirmed
    }

    const paymentType = consignmentDetailsResponse.order.paymentType;
    const creditId = consignmentDetailsResponse.businessUnitCompany?.creditDetails?.creditId;
    // Check if we should call credit capture API
    if (paymentType === PURCHASE_ORDER) {
      checkAndCaptureCreditReq.run(creditId, consignmentId, payload);
      return;
    }

    fulfillmentReq.run(consignmentId, payload);
    onModalClose();
    setIsReviewPage(false);
    setIsReviewPageMounted(false)
  };

  const splitBanner = <Banner
    margin='0'
    bgColor={Background.white}
    boxShadow={`0px 0px 15px ${Color.darkGray}`}
    loadSpinner
    title='Order split is in progress'
    text="Please wait, this can take up to a minute. Refresh the page to see the updates."
    textAreaMarginBottom="0"
    titleMarginBottom={Space.spBasePlus}
    border={false}
    spinnerColor={'black'}
  />

  const updates = liveUpdates ? splitBanner : null;

  const incorporatingEditChangesBanner = isIncorporatingEditChanges ? <Banner
    margin='0'
    bgColor={Background.white}
    boxShadow={`0px 0px 15px ${Color.darkGray}`}
    loadSpinner
    title='Edit order changes are being incorporated'
    text="Please wait, this usually takes a few seconds."
    textAreaMarginBottom="0"
    titleMarginBottom={Space.spBasePlus}
    border={false}
    spinnerColor={'black'}
  /> : null;

  const onConfirmSplitClick = () => {
    let entries: any[] = [];


    fulfillmentRequest.length > 0 && fulfillmentRequest.forEach((entry, i) => {
      let committedDate;
      let manufactureDate;

      if (entry.manufactureQuantity > 0) {
        manufactureDate = entry?.manufactureDate
          ? new Date(entry.manufactureDate).setUTCHours(0, 0, 0, 0)
          : new Date(entry.preliminaryDeliveryDate).setUTCHours(0, 0, 0, 0);
        committedDate = 0;
      } else if (entry.committedQuantity > 0) {
        committedDate = entry?.committedDate
          ? new Date(entry.committedDate).setUTCHours(0, 0, 0, 0)
          : new Date(entry.preliminaryDeliveryDate).setUTCHours(0, 0, 0, 0);
        manufactureDate = 0;
      } else {
        // Cancel-only or deleted entry — no committed/manufacture quantities
        // Set both dates to 0 to avoid overwriting user-changed dates during consolidation
        committedDate = 0;
        manufactureDate = 0;
      }

      let dd: any = {
        entryNumber: entry.entryNumber,
        quantity: entry.quantity,
        productId: entry?.productId,
        committedQuantity: entry?.committedQuantity || 0,
        manufactureQuantity: entry?.manufactureQuantity || 0,
        cancelQuantity: entry.cancelQuantity || 0,
        committedDate,
        manufactureDate,
      };

      if (consignmentDetailsResponse.consignmentType === CONSIGNMENT_TYPES.RENT) {
        dd = {
          ...dd,
          rentalStartDate: entry?.rentalStartDate
            ? new Date(entry.rentalStartDate).setUTCHours(0, 0, 0, 0)
            : 0,
          rentalEndDate: entry?.rentalEndDate
            ? new Date(entry.rentalEndDate).setUTCHours(0, 0, 0, 0)
            : 0,
          committedDate: entry?.committedQuantity > 0
            ? new Date(entry.rentalStartDate).setUTCHours(0, 0, 0, 0)
            : 0,
          committedEndDate: entry?.committedQuantity > 0
            ? new Date(entry.rentalEndDate).setUTCHours(0, 0, 0, 0)
            : 0,
          manufactureDate: entry?.manufactureQuantity > 0
            ? new Date(entry.rentalStartDate).setUTCHours(0, 0, 0, 0)
            : 0,
          manufactureEndDate: entry?.manufactureQuantity > 0
            ? new Date(entry.rentalEndDate).setUTCHours(0, 0, 0, 0)
            : 0,
        };
      }

      // Check for duplicate entryNumber and update instead of pushing new
      const existingIndex = entries.findIndex(e => e.entryNumber === entry.entryNumber);
      if (existingIndex !== -1) {
        const existing = entries[existingIndex];

        entries[existingIndex] = {
          ...existing,
          // Keep the higher committedQuantity if current is 0
          committedQuantity: dd.committedQuantity !== 0 ? dd.committedQuantity : existing.committedQuantity,
          manufactureQuantity: dd.manufactureQuantity !== 0 ? dd.manufactureQuantity : existing.manufactureQuantity,
          cancelQuantity: dd.cancelQuantity !== 0 ? dd.cancelQuantity : existing.cancelQuantity,
          committedDate: dd.committedDate !== 0 ? dd.committedDate : existing.committedDate,
          manufactureDate: dd.manufactureDate !== 0 ? dd.manufactureDate : existing.manufactureDate,
          rentalStartDate: dd.rentalStartDate !== 0 ? dd.rentalStartDate : existing.rentalStartDate,
          rentalEndDate: dd.rentalEndDate !== 0 ? dd.rentalEndDate : existing.rentalEndDate,
          committedEndDate: dd.committedEndDate !== 0 ? dd.committedEndDate : existing.committedEndDate,
          manufactureEndDate: dd.manufactureEndDate !== 0 ? dd.manufactureEndDate : existing.manufactureEndDate,
          // Overwrite other values directly if they’re not sensitive (like quantity, productId)
          quantity: (existing.quantity || 0) + (dd.quantity || 0),
          productId: dd.productId,
        };
      } else {
        entries.push(dd);
      }
    });

    const modifiedPayload = entries.map((item: any) => {
      return {
        entryNumber: item.entryNumber,
        quantity: (item.committedQuantity || 0) + (item.manufactureQuantity || 0) + (item.cancelQuantity || 0),
        committedQuantity: item.committedQuantity,
        manufactureQuantity: item.manufactureQuantity,
        cancelQuantity: item.cancelQuantity,
        committedDate: item.committedDate || item.rentalStartDate || 0,
        manufactureDate: item.manufactureDate,
        rentalStartDate: item.rentalStartDate || 0,
        rentalEndDate: item.rentalEndDate || 0,
        committedEndDate: item.committedEndDate || item.rentalEndDate || 0,
        manufactureEndDate: item.manufactureEndDate || 0
      }
    });

    const splitConsignmentBody = {
      availableDate: consignmentDetailsResponse.availableDate ? new Date(consignmentDetailsResponse.availableDate).getTime() : 0,
      consignmentId: consignmentDetailsResponse.consignmentId,
      state: consignmentDetailsResponse.state,
      entries: modifiedPayload
    }

    setSplitConsignmentBody(splitConsignmentBody);

    if (hasSameStockAndManufactureDate(fulfillmentRequest)) {
      setIsFulfillmentConfirmModalOpen(true);
    } else {
      if (hasPostOrderShippingCost) {

        // Store the payload with original state for emulate API (closure ensures correct value)
        const payloadForEmulateApi = { ...splitConsignmentBody };

        // Build fulfillmentData from splitConsignmentBody.entries (already consolidated and correct)
        // Map from splitConsignmentBody to get correct dates, and match with fulfillmentRequest to get entry IDs
        const fulfillmentData = {
          consignmentEntries: splitConsignmentBody.entries
            .map((splitEntry: any) => {
              // Find non-deleted entry first, fallback to deleted entry for cancel-only entries
              const originalEntry = fulfillmentRequest.find(e => e.entryNumber === splitEntry.entryNumber && !e.isDeleted)
                || fulfillmentRequest.find(e => e.entryNumber === splitEntry.entryNumber);
              return {
                id: originalEntry?.id,
                committedQuantity: splitEntry.committedQuantity || 0,
                committedDate: splitEntry.committedDate || 0,
                manufactureQuantity: splitEntry.manufactureQuantity || 0,
                manufactureDate: splitEntry.manufactureDate || 0,
                cancelQuantity: splitEntry.cancelQuantity || 0,
                committedEndDate: splitEntry.committedEndDate || 0,
                manufactureEndDate: splitEntry.manufactureEndDate || 0,
                rentalStartDate: splitEntry.rentalStartDate || 0,
                rentalEndDate: splitEntry.rentalEndDate || 0
              };
            })
        };

        // Update splitConsignmentBody state to include the payload for later use
        setSplitConsignmentBody(payloadForEmulateApi);

        // Update state to AWAITS_SHIPPING with fulfillment data
        // Pass payloadForEmulateApi through a custom property so onResolve can access it
        (updateStateReq as any).tempSplitPayload = payloadForEmulateApi;
        updateStateReq.run(consignmentId, AWAITS_SHIPPING, null, null, [], null, null, fulfillmentData);
        setIsReviewPage(false);
        setIsReviewPageMounted(false)
      } else {
        setIsConfirmFulfillmentModalOpen(true);
      }
    }
  };

  const handleBackFromEmulateScreen = () => {
    // Call API to revert state from AWAITS_SHIPPING to PROCESSING
    // Backend will set previousState to ORDER_SENT_TO_OEM and clear fulfillment data
    updateStateReq.run(
      consignmentId,
      PROCESSING,      // Revert to PROCESSING
      null,            // cancellationReason
      null,            // cancelledBy
      [],              // stockQuantity
      null,            // reasonType
      null,            // availableDate
      null             // fulfillmentData - will be cleared by backend
    );

    // Reset UI state
    setIsFulfillmentStep(true);
    setIsInvalid(false);
    setIsPostOrderShippingCostSplitFormOpen(false);
    setAdditionalShippingCostsValues([]);
    window.scrollTo(0, 0);
  };

  const onConfirmDispatchModalClick = () => {
    dispatchUpdateReq.run(consignmentId, dispatchRequest);
    setShouldRedirect(false);
    onModalClose();
  };

  const onConfirmDelayModalClick = () => {
    const requestBody = isShipping ? shippingConsignmentRequest : pickupConsignmentRequest;

    deliveryUpdateReq.run(consignmentId, requestBody);
    setShouldRedirect(false);
    onModalClose();
  };

  const onConfirmDelayAccept = (state: string) => {
    const statesToCheck = [PACKING, MANUFACTURING];

    if (!statesToCheck.includes(state)) {
      state = isShipping ? READY_TO_SHIP : READY_FOR_PICKUP;
    }
    updateStateReq.run(consignmentId, state);
    setShouldRedirect(false);
    onModalClose();
  };

  const onConfirmManufacture = (newAvailableDate: string | null = null, state: string = PACKING) => {
    const statesToCheck = [PACKING, MANUFACTURING, READY_FOR_PICKUP, READY_TO_SHIP]

    if (!statesToCheck.includes(state)) return;
    updateStateReq.run(consignmentId, state, null, null, null, null, newAvailableDate);
    setShouldRedirect(false);
    onModalClose();
  };

  const onShippingCarrierSelect = (event: any) => {
    const foundShippingCarrier = shippingCarriers.find(shCarrier => shCarrier.name === event.target.value);

    if (foundShippingCarrier) {
      setShippingConsignmentRequest({ ...shippingConsignmentRequest, shippingCarrier: foundShippingCarrier });
    }
  };

  const onTrackingIdChange = (value: string) => {
    value = value?.trim();
    setShippingConsignmentRequest({ ...shippingConsignmentRequest, trackingId: value });
  };



  const onDeliveryDateChange = (value: any, newRentalStartDate?: string, newRentalEndDate?: string) => {
    const state = { ...shippingConsignmentRequest, deliveryDate: value }
    if (newRentalStartDate) state['rentalNewStartDate'] = newRentalStartDate;
    if (newRentalEndDate) state['rentalNewEndDate'] = newRentalEndDate

    setShippingConsignmentRequest(state);

  };

  const onReferenceIdChange = (value: string) => {
    value = value?.trim()
    setPickupConsignmentRequest({ ...pickupConsignmentRequest, referenceNumber: value });
  };

  const onPickupDateChange = (value: any, newRentalStartDate?: string, newRentalEndDate?: string) => {
    const state = { ...pickupConsignmentRequest, pickupDate: value }
    if (newRentalStartDate) state['rentalNewStartDate'] = newRentalStartDate;
    if (newRentalEndDate) state['rentalNewEndDate'] = newRentalEndDate

    setPickupConsignmentRequest(state);
  };

  const onDispatchDateChange = (value: string) => {
    setDispatchRequest({ ...dispatchRequest, dispatchDate: value });
  };


  const onPickedUpByChange = (value: string) => {
    setDispatchRequest({ ...dispatchRequest, pickedUpBy: value });
  };

  const setPreparationStep = (consignment: Cons.ConsignmentDetails) => {
    const isPackingState = consignment.state === PACKING && ![AWAITS_SHIPPING, CHECKING_CREDIT].includes(consignment.previousState);
    setIsPreparationStep(isPackingState);
  };

  const consignmentReq = useAPI({
    deferFn: loadConsignment,
    onResolve: result => {
      // An unfinished edit session (EDIT_ORDER snapshot exists, editOrderResult not
      // COMPLETED) with the order still in New status sends the user straight back to
      // the edit order screen — the session must be applied or discarded from there
      if (
        result.state === ORDER_SENT_TO_OEM &&
        result.consignmentType === CONSIGNMENT_TYPES.PURCHASE &&
        result.editOrderResult &&
        result.editOrderResult !== EDIT_ORDER_RESULTS.COMPLETED &&
        grantAccess(featureFlags, FeatureFlagValues.EDIT_ORDER, user!)
      ) {
        navigate(`/orders/order-details/${result.consignmentId.slice(4)}/edit-order`);
        return;
      }

      setConsignmentDetailsResponse({ ...result, deliveryType: result.consignmentEntries[0].deliveryMode });
      updateVendor(result.vendor.name!, result.consignmentEntries[0]?.brandVendor?.brandId);
      setOriginalDeliveryDate(result.availableDate);
      setOriginalReferenceNumber(result.referenceNumber || result.consignmentId.substring(4));
      setFulfillmentRequest(result.consignmentEntries);
      setShippingConsignmentRequest({
        ...shippingConsignmentRequest,
        shippingCarrier: result.shippingCarrier || '',
        trackingId: result.trackingId || '',
        deliveryDate: result.availableDate
      });
      setPickupConsignmentRequest({
        ...pickupConsignmentRequest,
        referenceNumber: result.referenceNumber || result.consignmentId.substring(4),
        pickupDate: result.availableDate
      });
      setDispatchRequest({
        dispatchDate: result.dispatchDate || new Date().toISOString(),
        pickedUpBy: result.pickedUpBy?.trim() || parseRecipientName(result.pickupContact)
      });
      setPreparationStep(result);
      setIsFulfillmentStep(result.state === PROCESSING);
      setIsAwaitingDispatchStep(result.state === SHIPPED_READY_FOR_PICKUP || result.state === READY_FOR_PICKUP || result.state === READY_TO_SHIP);
      setIsRentalStep([RENT_IN_PROGRESS, RENT_RETURN_PENDING, RENT_COMPLETED, MODIFIED_BY_CUSTOMER, MODIFIED_BY_VENDOR].includes(result.state));
      setIsFulfillmentCompleteStep(result.state === DELIVERED_PICKED_UP || result.state === SHIPPED || result.state === PICKED_UP);
      setIsDelayedStep(result.state === DELAYED);
      setIsShipping(result.consignmentEntries[0].deliveryMode === SHIPPING);

      if (result?.modificationEntry?.initiatedFrom === RENT_IN_PROGRESS) {
        setHiddenStates([...HIDE_CANCEL_CONSIGNMENT_STATES, MODIFIED_BY_CUSTOMER, MODIFIED_BY_VENDOR]);
      }
      else {
        setHiddenStates([...HIDE_CANCEL_CONSIGNMENT_STATES]);
      }

      if (result.consignmentEntries) {
        const mappedSerialNumbers = result.consignmentEntries.map(x => { return { consignmentEntryId: x.id, serialNumber: x.serialNumber } })
        setSerialNumbers(mappedSerialNumbers);
      }

      if (result.salesOrderNumber) {
        getSalesOrderSingle.run(result.salesOrderNumber);
      }
      shippingCarriersReq.run(result.countryId)
    },
    onReject: err => {
      renderToast(toast.TYPE.ERROR, err.message);
    }
  });

  const updateConsignmentSalesOrder = useAPI({
    deferFn: updateSalesOrderNumber,
    onResolve: (result) => {
      setConsignmentDetailsResponse({ ...consignmentDetailsResponse, salesOrderNumber: result.salesOrderNumber });
      setSalesOrderLoading(false);
      onModalClose();
      if (salesOrderError) {
        setSalesOrderResult({});
      }
      setSalesOrderError(false);
      setSalesOrderLoading(false);
    },
    onReject: err => {
      renderToast(toast.TYPE.ERROR, err.message);
      setSalesOrderResult({});
    }
  });

  const salesOrderUpdateFn = (salesOrderNumber: string) => {
    if (salesOrderNumber != "") {
      setSalesOrderLoading(true);
      getSalesOrder.run(salesOrderNumber);
    }
    else {
      saveSalesOrderNumber(salesOrderNumber);
    }
  }

  const saveSalesOrderNumber = (salesOrderNumber: string) => {
    setSalesOrderLoading(true);
    updateConsignmentSalesOrder.run(consignmentDetailsResponse.consignmentId, salesOrderNumber);
  }
  const getDocumentsReq = useAPI({
    deferFn: getDocumentsForConsignment,
    onResolve: result => {
      setDocuments(result);
    },
    onReject: err => {
      const message = typeof err?.message === 'string' ? err.message : 'Failed to load consignment documents';
      renderToast(toast.TYPE.ERROR, message);
    }
  });

  const getSalesOrderSingle = useAPI({
    deferFn: getSalesOrderForConsignment,
    onResolve: (result) => {
      setSalesOrderResult(result);
    },
    onReject: (err: any) => {
      if (err?.code != 404) {
        setSalesOrderTimeout(true);
      }
    },
  });

  const getSalesOrder = useAPI({
    deferFn: getSalesOrderForConsignment,
    onResolve: (result) => {
      renderToast(toast.TYPE.SUCCESS, 'Sales order data received');
      setSalesOrderResult(result);
      setSalesOrderError(false);
      setSalesOrderTimeout(false);
      setSalesOrderModalTimeout(false);
      setSalesOrderLoading(false);
      onModalClose();
      updateConsignmentSalesOrder.run(
        consignmentDetailsResponse.consignmentId,
        result[0].salesOrder
      );
    },
    onReject: (err: any) => {
      setSalesOrderLoading(false);
      if (err?.code == 404) {
        setSalesOrderError(true);
      } else {
        setSalesOrderTimeout(true);
        setSalesOrderModalTimeout(true);
      }
    },
  });

  const getDocumentsForOrderReq = useAPI({
    deferFn: getDocumentsForOrder,
    onResolve: result => {
      setOrderDocuments(result);
    },
    onReject: err => {
      const message = typeof err?.message === 'string' ? err.message : 'Failed to load order documents';
      renderToast(toast.TYPE.ERROR, message);
    }
  });

  const getTransactionCreditByCodeReq = useAPI({
    deferFn: getTransactionCreditByCode,
    onResolve: () => {
      // Transaction credit data will be available in the request state
    },
    onReject: err => {
      console.error('Failed to fetch transaction credit:', err.message);
      renderToast(toast.TYPE.ERROR, 'Failed to fetch credit transactions')
    }
  });

  useEffect(() => {
    setPreviousUrl(queryParams.navigateBack);
    setQueryParams({ navigateBack: undefined });
    consignmentReq.run(consignmentId, user?.vendorId);
    getDocumentsReq.run(consignmentId);

    // Load rejection reasons from session storage
    const storedRejectionReasons = loadRejectionReasonsFromStorage();
    if (storedRejectionReasons.length > 0) {
      const formattedReasons = formatRejectionReasons(storedRejectionReasons);
      setRejectionReasons(formattedReasons);
    }
  }, []);

  useEffect(() => {
    if (consignmentDetailsResponse.orderId) {
      getDocumentsForOrderReq.run(consignmentDetailsResponse.orderId);
    }
  }, [consignmentDetailsResponse.orderId]);

  // Memoize credit check parameters to prevent unnecessary API calls
  const creditCheckParams = useMemo(() => ({
    paymentType: consignmentDetailsResponse.order?.paymentType,
    creditId: consignmentDetailsResponse.businessUnitCompany?.creditId
  }), [
    consignmentDetailsResponse.order?.paymentType,
    consignmentDetailsResponse.businessUnitCompany?.creditId
  ]);

  // Fetch transaction credit data for credit check flow
  useEffect(() => {
    const { paymentType, creditId } = creditCheckParams;

    if (paymentType === PURCHASE_ORDER && creditId && consignmentId) {
      getTransactionCreditByCodeReq.run(creditId, consignmentId);
    }
  }, [creditCheckParams, consignmentId]);

  // State restoration: if consignment is in AWAITS_SHIPPING, rebuild split body and call emulate API
  useEffect(() => {

    if (consignmentDetailsResponse.state === AWAITS_SHIPPING && hasPostOrderShippingCost && consignmentDetailsResponse.consignmentEntries?.length > 0) {
      // Rebuild splitConsignmentBody from consignmentDetailsResponse
      const entries = consignmentDetailsResponse.consignmentEntries.map((entry) => {
        return {
          entryNumber: entry.entryNumber,
          quantity: (entry.committedQuantity || 0) + (entry.manufactureQuantity || 0) + (entry.cancelQuantity || 0),
          committedQuantity: entry.committedQuantity || 0,
          manufactureQuantity: entry.manufactureQuantity || 0,
          cancelQuantity: entry.cancelQuantity || 0,
          committedDate: entry.committedDate ? new Date(entry.committedDate).getTime() : 0,
          manufactureDate: entry.manufactureDate ? new Date(entry.manufactureDate).getTime() : 0,
          rentalStartDate: entry.rentalStartDate ? new Date(entry.rentalStartDate).getTime() : 0,
          rentalEndDate: entry.rentalEndDate ? new Date(entry.rentalEndDate).getTime() : 0,
          committedEndDate: entry.committedEndDate ? new Date(entry.committedEndDate).getTime() : 0,
          manufactureEndDate: entry.manufactureEndDate ? new Date(entry.manufactureEndDate).getTime() : 0
        };
      });

      const restoredSplitBody = {
        availableDate: consignmentDetailsResponse.availableDate ? new Date(consignmentDetailsResponse.availableDate).getTime() : 0,
        consignmentId: consignmentDetailsResponse.consignmentId,
        state: consignmentDetailsResponse.previousState,
        entries
      };

      setSplitConsignmentBody(restoredSplitBody);
      splitConsignmentReq.run(consignmentId, restoredSplitBody);
    }
  }, [consignmentDetailsResponse.state, consignmentDetailsResponse.consignmentId, hasPostOrderShippingCost]);

  // Memoize transaction credit data to prevent unnecessary processing
  const transactionCreditData = useMemo((): CreditTransaction[] | null => {
    const { paymentType } = creditCheckParams;

    if (paymentType === PURCHASE_ORDER && getTransactionCreditByCodeReq.data) {
      const response = getTransactionCreditByCodeReq.data as TransactionCreditResponse;
      return response?.transactions || [];
    }

    return null;
  }, [creditCheckParams, getTransactionCreditByCodeReq.data]);

  const shippingCarriersReq = useAPI({
    deferFn: loadShippingCarriersByCountry,
    onResolve: (result) => setShippingCarriers(result)
  });

  const updateConsignmentEntrySerialNumberReq = useAPI({
    deferFn: updateConsignmentEntrySerialNumber,
    onResolve: () => {
      renderToast(toast.TYPE.SUCCESS, 'Serial number updated successfully.');
    },
    onReject: err => {
      renderToast(toast.TYPE.ERROR, err.message);
    }
  });

  const updateStateReq = useAPI({
    deferFn: updateState,
    onResolve: (result) => {
      renderToast(toast.TYPE.SUCCESS, 'The order state is updated successfully.');
      consignmentReq.reload();
      // useEffect at lines 1407-1438 will automatically handle AWAITS_SHIPPING state
    },
    onReject: err => {
      renderToast(toast.TYPE.ERROR, err.message);
    }
  });

  const recheckCreditReq = useAPI({
    deferFn: recheckCredit,
    onResolve: () => {
      renderToast(toast.TYPE.SUCCESS, 'The credit recheck has been initiated.');

      consignmentReq.reload();
    },
    onReject: err => {
      renderToast(toast.TYPE.ERROR, err.message);
    }
  });

  const retrySalesOrderReq = useAPI({
    deferFn: retrySalesOrderCreation,
    onResolve: () => {
      renderToast(toast.TYPE.SUCCESS, 'The sales order retry has been initiated.');

      consignmentReq.reload();
    },
    onReject: err => {
      renderToast(toast.TYPE.ERROR, err.message);
    }
  });

  const cancelSalesOrderReq = useAPI({
    deferFn: cancelSalesOrder,
    onResolve: () => {
      renderToast(toast.TYPE.SUCCESS, 'Sales order cancellation has been initiated.');
      consignmentReq.reload();
    },
    onReject: err => {
      renderToast(toast.TYPE.ERROR, err.message);
    }
  });

  const retryCancelSalesOrderReq = useAPI({
    deferFn: retryCancelSalesOrder,
    onResolve: () => {
      renderToast(toast.TYPE.SUCCESS, 'Sales order cancellation retry has been initiated.');
      consignmentReq.reload();
    },
    onReject: err => {
      renderToast(toast.TYPE.ERROR, err.message);
    }
  });

  const overrideCreditTransactionReq = useAPI({
    deferFn: overrideCreditTransaction,
    onResolve: () => {
      renderToast(toast.TYPE.SUCCESS, 'Credit transaction override completed successfully.');

      // Fetch updated transactions after override
      const creditId = consignmentDetailsResponse.businessUnitCompany?.creditId;
      if (creditId && consignmentId) {
        getTransactionCreditByCodeReq.run(creditId, consignmentId);
      }

      consignmentReq.reload();
    },
    onReject: err => {
      renderToast(toast.TYPE.ERROR, err.message || 'Failed to override credit transaction.');
    }
  });

  const fulfillmentReq = useAPI({
    deferFn: updateFulfillment,
    onResolve: (result) => {

      renderToast(toast.TYPE.SUCCESS, 'The order details are updated successfully.');
      if (fulfillmentRequest.length > 1 && consignmentDetailsResponse.totalAdditionalShippingCost) {
        setLiveUpdates(true);
      } else if (fulfillmentRequest.length > 1 && consignmentDetailsResponse.consignmentType === CONSIGNMENT_TYPES.RENT && result.state === SPLIT_CONSIGNMENT) {
        setLiveUpdates(true);
        setIsFulfillmentStep(false);
      } else {
        consignmentReq.reload();
      }
    },
    onReject: err => {
      renderToast(toast.TYPE.ERROR, err.message);
    }
  });

  useEffect(() => {
    socket.on('updateConsignment', () => {
      if (hasPostOrderShippingCost) {
        onConfirmFulfillmentClick();
      }
    })

    return () => {
      socket.off('updateConsignment', () => {
        consignmentReq.reload();
        setLiveUpdates(false);
        navigate(`/orders/order-details/${consignmentDetailsResponse.consignmentId?.slice(4)}`);
      });
    }
  }, [socket]);

  useEffect(() => {
    socket.on('updateConsignment', () => {

      // Reload consignment if payment is not paid when on manufacturing and packing state for non rental order
      if (consignmentDetailsResponse.order.paymentType !== 'PURCHASE_ORDER') {
        if ([MANUFACTURING, PACKING].includes(consignmentDetailsResponse.state) && consignmentDetailsResponse.paymentStatus === 'NOT_PAID' && !isRental) {
          consignmentReq.reload();
        }
      }

      if (consignmentDetailsResponse.state === RENT_COMPLETED && consignmentDetailsResponse.paymentStatus === PAYMENT_STATUS.NOT_PAID) {
        consignmentReq.reload();
      }
    });

    return () => {
      socket.off('updateConsignment', () => {
        if ([MANUFACTURING, PACKING].includes(consignmentDetailsResponse.state) && consignmentDetailsResponse.paymentStatus === 'PAID') {
          consignmentReq.reload();
        }
      });
    }
  }, [socket, consignmentDetailsResponse]);

  // DS's ORDER_SENT_TO_OEM / ORDER_SENT_TO_OEM reply lifts the block above
  const onOrderSyncCompleted = useCallback((data: any) => {
    if (!isOrderSyncPending || data?.consignmentId !== consignmentDetailsResponse?.consignmentId) return;

    consignmentReq.reload();
  }, [isOrderSyncPending, consignmentDetailsResponse?.consignmentId]);

  useEffect(() => {
    socket.on('updateConsignment', onOrderSyncCompleted);

    return () => {
      socket.off('updateConsignment', onOrderSyncCompleted);
    };
  }, [socket, onOrderSyncCompleted]);

  const onBuyerEditDecision = useCallback((data: any) => {
    if (data?.consignmentId !== consignmentDetailsResponse?.consignmentId) return;
    if (consignmentDetailsResponse?.state !== AWAITS_EDIT_APPROVAL) return;

    consignmentReq.reload();
  }, [consignmentDetailsResponse?.consignmentId, consignmentDetailsResponse?.state]);

  useEffect(() => {
    socket.on('updateConsignment', onBuyerEditDecision);

    return () => {
      socket.off('updateConsignment', onBuyerEditDecision);
    };
  }, [socket, onBuyerEditDecision]);

  const splitConsignmentReq = useAPI({
    deferFn: emulateSplitConsignment,
    onResolve: (result) => {
      const splitedConsignments = result.map((splitConsignment: any) => {
        const newEntries = splitConsignment.entries.map((entry: any) => {
          let foundEntry = consignmentDetailsResponse.consignmentEntries.find(consEntry => consEntry.entryNumber === entry.entryNumber);
          if (!foundEntry) {
            foundEntry = consignmentDetailsResponse.consignmentEntriesAddons.find(consEntry => consEntry.entryNumber === entry.entryNumber);
          }
          const selectableAttributes = foundEntry && foundEntry.selectableAttributes;
          const attributeValues = foundEntry && foundEntry.attributeValues;
          const productImages = foundEntry && foundEntry.productImages;
          const priceBook = foundEntry?.priceBook;
          const pricebookId = foundEntry?.pricebookId;
          const pricebookName = foundEntry?.pricebookName;
          const productVariantPrices = foundEntry?.productVariantPrices;
          const parentEntryNumber = foundEntry?.parentEntryNumber;
          const serialNumber = foundEntry?.serialNumber;
          const entryNumber = foundEntry?.entryNumber;
          const plannedUsage = foundEntry?.plannedUsage;
          const unitOfMeasurement = foundEntry?.unitOfMeasurement;
          const sku = entry?.productSKU;
          const materialApiValidation = foundEntry?.materialApiValidation;
          const affiliateCode = foundEntry?.affiliateCode;
          const commissionPercentage = foundEntry?.commissionPercentage;
          const commissionAmount = foundEntry?.commissionAmount;
          const customerAffiliate = foundEntry?.customerAffiliate;

          return {
            ...entry, selectableAttributes,
            attributeValues, productImages, priceBook, pricebookId, pricebookName, productVariantPrices,
            parentEntryNumber, serialNumber, entryNumber, plannedUsage, unitOfMeasurement, sku, materialApiValidation,
            affiliateCode, commissionPercentage, commissionAmount, customerAffiliate
          }
        })
        return { ...splitConsignment, entries: newEntries }
      });

      const shippingCostsValues = result.map((consignment: any, index: number) => {
        const splitConsignment = splitedConsignments[index];
        return {
          consignmentId: splitConsignment?.consignmentId,
          state: consignment.state,
          estimatedAvailableDate: consignment.availableDate || consignmentDetailsResponse.availableDate,
          additionalShippingCost: 0,
          entryNumbers: consignment.entries?.map((entry: any) => entry.entryNumber),
        }
      })

      setAdditionalShippingCostsValues(shippingCostsValues);

      setSplitConsignments(splitedConsignments);
      setIsFulfillmentStep(false);
      setIsPostOrderShippingCostSplitFormOpen(true);
    },
    onReject: err => {
      renderToast(toast.TYPE.ERROR, err.message);
    }
  });

  const deliveryUpdateReq = useAPI({
    deferFn: updateDelivery,
    onResolve: result => {
      const notificationMessage = 'Delivery information has been updated. Customer will be notified by email.'
      renderToast(toast.TYPE.SUCCESS, notificationMessage);
      consignmentReq.reload();
    },
    onReject: err => {
      renderToast(toast.TYPE.ERROR, err.message);
    }
  });

  const dispatchUpdateReq = useAPI({
    deferFn: updateDispatch,
    onResolve: () => {
      renderToast(toast.TYPE.SUCCESS, 'The order details are updated successfully.');

      consignmentReq.reload();
    },
    onReject: err => {
      renderToast(toast.TYPE.ERROR, err.message);
    }
  });

  const cancellationOrderEmailReq = useAPI({
    deferFn: cancellationOrderEmailRequest,
    onResolve: () => {
      renderToast(toast.TYPE.SUCCESS, 'Successfully sent');
      consignmentReq.reload();
    },
    onReject: (err: any) => renderToast(toast.TYPE.ERROR, err.message)
  });

  useEffect(() => {
    const startDateChanged = formatDateTime(rentalStartDate!) !== formatDateTime(consignmentDetailsResponse.rentalStartDate);
    const endDateChanged = formatDateTime(rentalEndDate!) !== formatDateTime(consignmentDetailsResponse.rentalEndDate);

    const estDate = consignmentDetailsResponse.deliveryType === 'SHIPPING' ? shippingConsignmentRequest.deliveryDate : pickupConsignmentRequest.pickupDate;
    const carrier = shippingConsignmentRequest.shippingCarrier?.name;
    const trackingId = consignmentDetailsResponse.deliveryType === 'SHIPPING' ? shippingConsignmentRequest.trackingId : pickupConsignmentRequest.referenceNumber

    if ((rentalStartDate && rentalStartDate) && (startDateChanged || endDateChanged)) {
      const startDate = new Date(rentalStartDate!);
      const endDate = new Date(rentalEndDate!);
      const estimatedDate = new Date(estDate!);

      if (estimatedDate <= startDate && startDate <= endDate) {
        setIsDisabled(false);
      } else {
        setIsDisabled(true);
      }
    } else if (estDate && trackingId) {
      if (consignmentDetailsResponse.deliveryType === 'SHIPPING') {
        if (carrier) {
          setIsDisabled(false);
        } else {
          setIsDisabled(true);
        }
      } else {
        setIsDisabled(false);
      }
    } else {
      setIsDisabled(true);
    }
  }, [rentalStartDate, rentalEndDate, consignmentDetailsResponse, shippingConsignmentRequest, pickupConsignmentRequest, showModal]);


  const refreshConsignment = () => {
    consignmentReq.run(consignmentId, user?.vendorId);
  }

  const updateRentalStateReq = useAPI({
    deferFn: updateRentalState,
    onResolve: () => {
      renderToast(toast.TYPE.SUCCESS, TEXT_LABELS.ORDER_UPDATED_SUCCESSFULLY);
      refreshConsignment();
      setLoader(false);
    },
    onReject: err => {
      renderToast(toast.TYPE.ERROR, err.message);
      setLoader(false);
    }
  });

  const createCreditRecordReq = useAPI({
    deferFn: createCreditRecord,
    onResolve: () => {
      renderToast(toast.TYPE.SUCCESS, TEXT_LABELS.MDM_NUMBER_ADDED);
      setShowAutomatedCC(false);
    },
    onReject: (err: any) => setMdmErrorMessage(TEXT_LABELS.INVALID_MDM_NUMBER)
  });

  const updateCreditReq = useAPI({
    deferFn: updateCreditRecord,
    onResolve: () => {
      renderToast(toast.TYPE.SUCCESS, TEXT_LABELS.MDM_NUMBER_ADDED);
      setShowAutomatedCC(false);
    },
    onReject: (err: any) => setMdmErrorMessage(TEXT_LABELS.INVALID_MDM_NUMBER)
  });

  const checkAndCaptureCreditReq = useAPI({
    deferFn: checkAndCaptureCredit,
    onResolve: () => {
      renderToast(toast.TYPE.SUCCESS, 'Credit check initiated successfully. Please wait for processing to complete.');
      // No need to call fulfillment API separately - new API handles everything
      onModalClose();
      setIsReviewPage(false);
      setIsReviewPageMounted(false);
      // Refresh the page to show CHECKING_CREDIT state and banner
      refreshConsignment();
    },
    onReject: (err: any) => {
      renderToast(toast.TYPE.ERROR, err.message || 'Credit check initiation failed');
      onModalClose();
    }
  });

  // The fulfilment form holds the entry ids loaded with the page. After an edit approval Hybris
  // renumbers the entries and they are re-created under new ids, so re-read the order when it
  // changes, or Confirm posts ids that no longer exist. Once the seller is past the form, a
  // re-read would swap in entries without their split, so leave it alone.
  const onProcessingOrderUpdated = useCallback((data: any) => {
    if (data?.consignmentId !== consignmentDetailsResponse?.consignmentId) return;
    if (consignmentDetailsResponse?.state !== PROCESSING) return;

    const isSubmittingSplit = isReviewPage
      || isFulfillmentConfirmModalOpen
      || isConfirmFulfillmentModalOpen
      || isPostOrderShippingCostSplitFormOpen
      || splitConsignmentReq.isLoading
      || updateStateReq.isLoading
      || fulfillmentReq.isLoading
      || checkAndCaptureCreditReq.isLoading;
    if (isSubmittingSplit) return;

    consignmentReq.reload();
  }, [
    consignmentDetailsResponse?.consignmentId,
    consignmentDetailsResponse?.state,
    isReviewPage,
    isFulfillmentConfirmModalOpen,
    isConfirmFulfillmentModalOpen,
    isPostOrderShippingCostSplitFormOpen,
    splitConsignmentReq.isLoading,
    updateStateReq.isLoading,
    fulfillmentReq.isLoading,
    checkAndCaptureCreditReq.isLoading
  ]);

  useEffect(() => {
    socket.on('updateConsignment', onProcessingOrderUpdated);

    return () => {
      socket.off('updateConsignment', onProcessingOrderUpdated);
    };
  }, [socket, onProcessingOrderUpdated]);

  const reverseConsignmentReq = useAPI({
    deferFn: reverseConsignment,
    onResolve: () => {
      renderToast(toast.TYPE.SUCCESS, 'Cancellation has been reversed successfully.');
      consignmentReq.reload();
    },
    onReject: err => {
      renderToast(toast.TYPE.ERROR, err.message);
    }
  });

  const loadSubscriptionCompanyReq = useAPI({
    deferFn: loadCompanyDefer,
    onResolve: (company: Company) => {
      setSubscriptionCompany(company);
      if (company.vendor) {
        setSubscriptionVendor(company.vendor as any);
      }
      if (company.businessUnit) {
        setSubscriptionBusinessUnit(company.businessUnit);
      }
    },
    onReject: (err: Error) => renderToast(toast.TYPE.ERROR, err.message)
  });

  const subscriptionPeriod = consignmentDetailsResponse.consignmentEntries?.[0]?.duration || 1;

  const updateSubscriptionStateReq = useAPI({
    deferFn: updateSubscriptionState,
    onResolve: () => {
      consignmentReq.reload();
    },
    onReject: (err: Error) => renderToast(toast.TYPE.ERROR, err.message)
  });

  const assignBUReq = useAPI({
    deferFn: assignBusinessUnitToCompany,
    onResolve: () => {
      const startDate = DateTime.now().toISO();
      const endDate = DateTime.now().plus({ months: subscriptionPeriod }).toISO();
      updateSubscriptionStateReq.run(
        consignmentDetailsResponse.consignmentId,
        PROCESSING,
        startDate,
        endDate,
        subscriptionPeriod
      );
    },
    onReject: (err: Error) => renderToast(toast.TYPE.ERROR, err.message)
  });

  const {
    vendorOptions: subscriptionVendorOptions,
    businessUnitOptions: subscriptionBUOptions,
    filteredBusinessUnits: subscriptionFilteredBUs
  } = useVendorBusinessUnitSelection({
    vendorsList,
    businessUnitList,
    selectedVendor: subscriptionVendor,
    selectedBusinessUnit: subscriptionBusinessUnit
  });

  const subscriptionBUDropdownOptions = subscriptionBUOptions.filter((opt: any) => opt.id !== 'all');

  const handleRefresh = () => {
    setRentalStartDate(null);
    setRentalEndDate(null);
    setReason('');
    setShowModal(false);
    setShippingConsignmentRequest({
      ...shippingConsignmentRequest,
      shippingCarrier: consignmentDetailsResponse?.shippingCarrier || '',
      trackingId: consignmentDetailsResponse?.trackingId || '',
      deliveryDate: consignmentDetailsResponse?.availableDate
    });
    setPickupConsignmentRequest({
      ...pickupConsignmentRequest,
      referenceNumber: consignmentDetailsResponse?.referenceNumber || consignmentDetailsResponse?.consignmentId.substring(4),
      pickupDate: consignmentDetailsResponse?.availableDate
    });
  }

  const handleSubmit = () => {
    setShowModal(false);

    const rentalStart = toDate(rentalStartDate!);
    const rentalEnd = toDate(rentalEndDate!);
    const consignmentStart = toDate(consignmentDetailsResponse.rentalStartDate);
    const consignmentEnd = toDate(consignmentDetailsResponse.rentalEndDate);

    if (
      rentalStart &&
      rentalEnd &&
      (rentalStart.getTime() !== consignmentStart?.getTime() ||
        rentalEnd.getTime() !== consignmentEnd?.getTime())
    ) {
      setLoader(true);
      const trackingId = consignmentDetailsResponse.deliveryType === SHIPPING ? shippingConsignmentRequest.trackingId : pickupConsignmentRequest.referenceNumber;
      const estDate = consignmentDetailsResponse.deliveryType === SHIPPING ? shippingConsignmentRequest.deliveryDate : pickupConsignmentRequest.pickupDate;
      const carrier = shippingConsignmentRequest.shippingCarrier;
      updateRentalStateReq.run(consignmentDetailsResponse?.consignmentId, RENT_UPDATED, null, rentalStartDate, rentalEndDate, reason, null, null, trackingId, estDate, carrier);
    } else {
      if (consignmentDetailsResponse.state === PACKING) {
        onContinueClick(false);
      } else {
        isFulfillmentCompleteStep ? onViewPaymentStatus() : onConfirmOrderInfoUpdate();
      }
    }
  }

  const handleCreditUpsert = () => {
    const businessUnitCompany = consignmentDetailsResponse?.businessUnitCompany;

    if (businessUnitCompany?.creditId) {
      const payload = {
        ...businessUnitCompany?.creditDetails,
        source: CREDIT_SOURCES.MDM,
        buyerExternalCode: mdmNumber
      };

      updateCreditReq.run(businessUnitCompany.creditId, payload);
    } else if (businessUnitCompany) {
      // Create credit for business unit
      const businessUnitCode = businessUnitCompany?.businessUnit?.code;
      const businessUnitCompanyId = businessUnitCompany?.id;
      const businessUnitId = businessUnitCompany?.businessUnitId;

      createCreditRecordReq.run(businessUnitCode, mdmNumber, null, null, consignmentDetailsResponse?.order?.companyId, CREDIT_SOURCES.MDM, businessUnitId, businessUnitCompanyId);
    }
  }

  useEffect(() => {
    handleRefresh();
  }, [consignmentDetailsResponse])

  useEffect(() => {
    if (isSubscription && consignmentDetailsResponse.state === ORDER_SENT_TO_OEM && consignmentDetailsResponse.order.companyId) {
      loadSubscriptionCompanyReq.run(consignmentDetailsResponse.order.company?.id);
    }
  }, [consignmentDetailsResponse.consignmentId, isSubscription]);

  const onSubscriptionAcceptClick = () => {
    const companyId = consignmentDetailsResponse.order.companyId;
    const isBUAlreadySet = !!(subscriptionCompany?.businessUnitId);
    const startDate = DateTime.now().toISO();
    const endDate = DateTime.now().plus({ months: subscriptionPeriod }).toISO();
    if (!isBUAlreadySet && subscriptionBusinessUnit?.id) {
      assignBUReq.run(subscriptionBusinessUnit.id, companyId);
    } else {
      updateSubscriptionStateReq.run(
        consignmentDetailsResponse.consignmentId,
        PROCESSING,
        startDate,
        endDate,
        subscriptionPeriod
      );
    }
  };

  const disableCancelConsignmentButton = () => {
    return hiddenStates.includes(consignmentDetailsResponse.state);
  }

  if (
    shippingCarriersReq.isLoading ||
    consignmentReq.isLoading ||
    updateStateReq.isLoading ||
    fulfillmentReq.isLoading ||
    deliveryUpdateReq.isLoading ||
    dispatchUpdateReq.isLoading ||
    overrideCreditTransactionReq.isLoading ||
    retryCancelSalesOrderReq.isLoading ||
    cancelSalesOrderReq.isLoading ||
    checkAndCaptureCreditReq.isLoading
  ) {
    return <LoadingIndicator center />;
  }

  const modal = (
    <OrderCancelModal
      show={isModalOpen}
      onClose={onModalClose}
      onConfirm={onConfirmDeclineModalClick}
      title="Are you sure you want to decline this order?"
      label="Please, provide reason for declining"
      confirmButtonText="Confirm decline"
      cancelButtonText="Cancel"
    />
  );

  const sumCanceledItems = consignmentDetailsResponse.consignmentEntries.reduce((total, entry) => {
    return total + entry.quantity;
  }, 0);

  const declinePostOrderShippingCostModal = (
    <OrderCancelModal
      show={isDeclineModalOpen}
      onClose={onModalClose}
      onConfirm={onConfirmDeclineModalClick}
      title="Are you sure you want to decline this order?"
      theme='error'
      borderColor={Border.main}
      color={Shade.shade550}
      label={`${consignmentDetailsResponse.consignmentEntries[0] ? sumCanceledItems : ''} items will be canceled. This action can not be undone`}
      label2='Please, provide reason for declining'
      confirmButtonText="Confirm decline"
      cancelButtonText="Back to items"
    />
  );

  const confirmFulfillmentModal = (
    <Modal
      showModal={isConfirmFulfillmentModalOpen}
      onClose={cancelConfirmFulfillmentModal}
      onClick={onConfirmFulfillmentModalClick}
      title="Order fulfillment"
      primaryButtonText="Process order"
    >
      <FlexboxContainer>
        <Span>
          All changes are final and cannot be undone. Please confirm your action.
        </Span>
      </FlexboxContainer>
    </Modal>
  );

  const modalOnHold = (
    <OrderCancelModal
      show={isModalOnHoldOpen}
      onClose={onModalClose}
      onConfirm={handleOnHoldBannerDeclineConfirmation}
      title="Are you sure you want to cancel this order?"
      label="Please, provide reason for canceling"
      confirmButtonText="Confirm cancel"
      cancelButtonText="Back"
    />
  );

  const dispatchModal = (
    <Modal
      showModal={isDispatchModalOpen}
      onClose={onModalClose}
      onClick={onConfirmDispatchModalClick}
      title="Order dispatch"
      primaryButtonText="Confirm dispatch"
    >
      <FlexboxContainer flexDirection="column">
        <Span>
          All changes are final and cannot be undone and the buyer will be notified if action is confirmed.
        </Span>
      </FlexboxContainer>
    </Modal>
  );

  const onConfirmShippingUpdate = () => {
    setIsManufactureModalOpen(false);
    setIsDelayModalOpen(true);
  };

  const handleUpdateOrderInfoModalClose = () => {
    setOpenUpdateOrderModal(false);
    onDeliveryDateChange(originalDeliveryDate);
    onPickupDateChange(originalDeliveryDate);
    onReferenceIdChange(originalReferenceNumber!);
  }

  const onConfirmOrderInfoUpdate = () => {
    const requestBody = isShipping ? shippingConsignmentRequest : pickupConsignmentRequest;

    if (
      (isShipping && shippingConsignmentRequest.deliveryDate && new Date(shippingConsignmentRequest.deliveryDate).setUTCHours(0, 0, 0, 0) > new Date(consignmentDetailsResponse.availableDate).setUTCHours(0, 0, 0, 0)) ||
      (consignmentDetailsResponse.availableDate && !isShipping && pickupConsignmentRequest.pickupDate && new Date(pickupConsignmentRequest.pickupDate).setUTCHours(0, 0, 0, 0) > new Date(consignmentDetailsResponse.availableDate).setUTCHours(0, 0, 0, 0))
    ) {
      setOpenUpdateOrderModal(false)
      setIsDelayModalOpen(true);
    } else {
      deliveryUpdateReq.run(consignmentId, requestBody);
      setShouldRedirect(false);
      setOpenUpdateOrderModal(false);
    }
  };

  // generic delay modal
  const manufactureModal = (
    <ManufactureModal
      show={isManufactureModalOpen}
      onClose={onModalClose}
      onConfirmCancel={onConfirmCancelModalClick}
      onConfirmShippingUpdate={onConfirmShippingUpdate}
      onConfirmManufacture={onConfirmManufacture}
      title={`Indicate how to proceed due to ${(consignmentDetailsResponse.previousState === MANUFACTURING || consignmentDetailsResponse.state === MANUFACTURING) ? "manufacturing" : "the"} delay`}
      warningMessage=""
      confirmButtonText="Continue"
      shippingDate={consignmentDetailsResponse.availableDate}
      rentalStartDate={consignmentDetailsResponse.rentalStartDate}
      rentalEndDate={consignmentDetailsResponse.rentalEndDate}
      isRental={isRental}
      consignmentEntries={consignmentDetailsResponse.consignmentEntries}
      shippingConsignmentRequest={shippingConsignmentRequest}
      pickupConsignmentRequest={pickupConsignmentRequest}
      setShippingConsignmentRequest={setShippingConsignmentRequest}
      setPickupConsignmentRequest={setPickupConsignmentRequest}
      deliveryUpdateReq={deliveryUpdateReq}
      consignmentId={consignmentId}
      deliveryType={consignmentDetailsResponse.deliveryType}
      state={consignmentDetailsResponse.state!}
      previousState={consignmentDetailsResponse.previousState!}
      salesOrderNumber={consignmentDetailsResponse.salesOrderNumber}
      handleShowCancellationConsignmentPopup={handleShowCancellationConsignmentPopup}
    />
  );

  const pickupModal = (
    <Modal
      showModal={isPickupModalOpen}
      onClose={onModalClose}
      onClick={onConfirmDispatchModalClick}
      title="Order pickup"
      primaryButtonText="Confirm pickup"
    >
      <FlexboxContainer flexDirection="column">
        <Span>
          All changes are final and cannot be undone and the buyer will be notified if action is confirmed.
        </Span>
      </FlexboxContainer>
    </Modal>
  );

  const delayModal = (
    <Modal
      title={isShipping ? 'Confirm shipping update' : 'Confirm pickup update'}
      onClose={onModalClose}
      showModal={isDelayModalOpen}
      theme="accent"
      onClick={() => onConfirmDelayModalClick()}
      primaryButtonText="Confirm update"
    >
      <FlexboxContainer flexDirection="column">
        <Span>
          {`Due to update to the ${isShipping ? 'ship' : 'ready for pick-up'} date customer will have 24 hours to cancel this order`}
        </Span>
      </FlexboxContainer>
    </Modal>
  );

  const delayAcceptModal = (
    <Modal
      showModal={isDelayedAcceptModalOpen}
      onClose={onModalClose}
      onClick={() => onConfirmDelayAccept(consignmentDetailsResponse.previousState)}
      title="Delayed order acceptance"
      primaryButtonText="Confirm acceptance"
    >
      <FlexboxContainer flexDirection="column">
        <Span>
          All changes are final and cannot be undone. Please confirm acceptance from the buyer.
        </Span>
      </FlexboxContainer>
    </Modal>
  );

  const newOrderButtons = (
    <FlexboxContainer
      flexDirection='row'
      justifyContent='flex-end'
      align-items="flex-end"
    >
      {
        canEditOrder &&
        <Button
          // Navigates to the edit order screen — the consignment state stays
          // ORDER_SENT_TO_OEM while the user edits
          onClick={() => navigate(`/orders/order-details/${consignmentDetailsResponse.consignmentId.slice(4)}/edit-order`)}
          bgColor={Background.navy}
          borderColor={Background.navy}
          margin={`0 ${Space.spHalf}`}
          permissionCode={PermissionCode.EDIT_ORDERS}
          disabled={isOrderSyncPending}
        >
          {TEXT_LABELS.EDIT_ORDER}
        </Button>
      }
      <Button
        isGhost
        theme='error'
        color={Main.error}
        margin={`0 ${Space.spHalf}`}
        onClick={onDeclineClick}
        permissionCode={PermissionCode.ACCEPT_ORDERS}
        disabled={isOrderSyncPending}
      >
        Decline order
      </Button>
      <Button
        onClick={() => {
          onAcceptClick(PROCESSING)
        }}
        borderColor={Main.brandDark}
        bgColor={Main.brandDark}
        permissionCode={PermissionCode.ACCEPT_ORDERS}
        disabled={isOrderSyncPending}
      >
        Accept order
      </Button>

    </FlexboxContainer>
  );


  const mdmSetupComponentContent = (
    <>
      {
        isRental &&
        <span>{TEXT_LABELS.RENT_MEASUREMENT_UNIT_INFO}</span>
      }
      {
        showAutomatedCC &&
        consignmentDetailsResponse.vendor?.isSalesApiEnabled &&
        consignmentDetailsResponse.businessUnitCompany?.creditDetails?.source !== CREDIT_SOURCES.MDM &&
        consignmentDetailsResponse.order?.paymentType === PURCHASE_ORDER &&
        <MDMNumberSetup
          validateMDMNumber={handleCreditUpsert}
          mdmNumber={mdmNumber}
          setMdmNumber={setMdmNumber}
          enableAutomaticCreditCheck={setEnableAutomatedCreditCheck}
          isAutomaticCreditCheckEnabled={enableAutomatedCreditCheck}
          mdmErrorMessage={mdmErrorMessage}
          disabled={!enableAutomatedCreditCheck}
          setMdmErrorMessage={setMdmErrorMessage}
          isVerifying={createCreditRecordReq.isLoading}
        />
      }
    </>
  );

  const mdmSetupComponent = (
    <FlexboxContainer flexDirection='column' gap='10px'>
      {mdmSetupComponentContent}
    </FlexboxContainer>
  );

  const orderActions = !isSubscription && consignmentDetailsResponse.state === ORDER_SENT_TO_OEM && consignmentDetailsResponse.order.customer.dowJonesStatus !== DOW_JONES_BLOCKED
    ? (
      <Banner
        bgColor={Background.white}
        boxShadow='0px 8px 16px rgba(47, 66, 83, 0.25), 0px 16px 20px rgba(47, 66, 83, 0.15)'
        title={`New ${isAffiliateProgram ? 'affiliate program' : ''} order`}
      >
        <FlexboxContainer flexDirection='column' gap='10px'>
          {
            React.Children.toArray(mdmSetupComponentContent?.props?.children)?.filter(Boolean)?.length > 0 &&
            <WarningBanner message={mdmSetupComponent} fontSize='16px' margin='20px 0px 0px 0px' />
          }
          All pre-order checks have been successfully completed.
          <br />{isAffiliateProgram ? 'Upon accepting this affiliate order, the buyer will gain access to selected products eligible for commission. The buyer will be able to promote these products and earn commission on successful sales. Detailed commission balance and performance can be tracked in the Members section under the Affiliate Program menu.'
           : 'If you are able to fullfill one or more line items please accept the order below.'}
          {isAffiliateProgram && (
            <AffiliateFormSubmitted
              documents={documents}
              isAffiliateFormPdfGenerated={consignmentDetailsResponse.isAffiliateFormPdfGenerated}
              submittedOn={consignmentDetailsResponse.affiliateFormSubmittedOn}
              isVisible={consignmentDetailsResponse.hasCognitoForm}
            />
          )}
          {isOrderSyncPending && (
            <WarningBanner message={TEXT_LABELS.ORDER_SYNC_PENDING} fontSize='14px' margin={`${Space.spBase} 0 0`} />
          )}
          <FlexboxContainer width='100%' justifyContent='space-between' alignItems='flex-end'>
            {isRental ? <RentalDates consignmentData={consignmentDetailsResponse} datesFormat='yyyy MMM dd' margin='0px' /> : <div></div>}
            {newOrderButtons}
          </FlexboxContainer>
        </FlexboxContainer>
      </Banner>
    ) : null;

  const subscriptionOrderActions = isSubscription && consignmentDetailsResponse.state === ORDER_SENT_TO_OEM && consignmentDetailsResponse.order.customer.dowJonesStatus !== DOW_JONES_BLOCKED
    ? (
      <Banner
        bgColor={Background.white}
        boxShadow='0px 8px 16px rgba(47, 66, 83, 0.25), 0px 16px 20px rgba(47, 66, 83, 0.15)'
        title='New subscription order'
      >
        <FlexboxContainer flexDirection='column' gap='10px'>
          <span>
            All pre-order checks have been successfully completed.<br />
            If you are able to fulfill one or more line items please accept the order below.<br />
            Subscription orders are recurring. The start and end dates indicate the duration of the
            current paid subscription term, after which the subscription renews automatically.
          </span>
          <FlexboxContainer gap='8px' margin='16px 0 0 0'>
            <DateFieldContainer>
              <LabelText>Subscription start date</LabelText>
              <DisabledField>{DateTime.now().toFormat('yyyy MMM dd')}</DisabledField>
            </DateFieldContainer>
            <DateFieldContainer>
              <LabelText>Subscription end date</LabelText>
              <DisabledField>
                {DateTime.now()
                  .plus({ months: subscriptionPeriod })
                  .toFormat('yyyy MMM dd')}
              </DisabledField>
            </DateFieldContainer>
          </FlexboxContainer>
          <FlexboxContainer gap='8px' margin='8px 0 0 0'>
            <VendorSelectionDropdown
              selectedVendorName={subscriptionVendor?.name || ''}
              vendorOptions={subscriptionVendorOptions}
              disabled={loadSubscriptionCompanyReq.isLoading || !!(subscriptionCompany?.businessUnitId)}
              onVendorChange={(vendorName: string) => {
                const vendor = vendorsList.find((v: Vendor) => v.name === vendorName) || null;
                setSubscriptionVendor(vendor);
                setSubscriptionBusinessUnit(null);
              }}
            />
            <BusinessUnitSelectionDropdown
              selectedBusinessUnitName={subscriptionBusinessUnit?.name || ''}
              businessUnitOptions={subscriptionBUDropdownOptions}
              disabled={loadSubscriptionCompanyReq.isLoading || !!(subscriptionCompany?.businessUnitId)}
              onBusinessUnitChange={(buId: string | null) => {
                if (!buId) {
                  setSubscriptionBusinessUnit(null);
                  return;
                }
                const bu = subscriptionFilteredBUs.find((b: BusinessUnit) => b.id?.toString() === buId) || null;
                setSubscriptionBusinessUnit(bu);
              }}
              showAllOption={false}
            />
          </FlexboxContainer>
          <FlexboxContainer width='100%' justifyContent='flex-end' margin='8px 0 0 0' gap='4px'>
            <Button
              isGhost
              theme='error'
              color={Main.error}
              margin={`0 ${Space.spHalf}`}
              onClick={onDeclineClick}
              permissionCode={PermissionCode.ACCEPT_ORDERS}
              disabled={!subscriptionVendor || !subscriptionBusinessUnit || assignBUReq.isLoading || updateSubscriptionStateReq.isLoading}
            >
              Decline order
            </Button>
            <Button
              onClick={onSubscriptionAcceptClick}
              borderColor={Main.brandDark}
              bgColor={Main.brandDark}
              disabled={!subscriptionVendor || !subscriptionBusinessUnit || assignBUReq.isLoading || updateSubscriptionStateReq.isLoading}
              permissionCode={PermissionCode.ACCEPT_ORDERS}
            >
              {assignBUReq.isLoading || updateSubscriptionStateReq.isLoading ? 'Accepting...' : 'Accept order'}
            </Button>
          </FlexboxContainer>
        </FlexboxContainer>
      </Banner>
    ) : null;

  const orderOnHoldAfterCheckingCreditActions = consignmentDetailsResponse.state === ON_HOLD && consignmentDetailsResponse.order.customer.dowJonesStatus !== DOW_JONES_BLOCKED
    ? (
      <CreditCheckV2Banner
        consignmentDetailsResponse={consignmentDetailsResponse}
        refreshConsignment={refreshConsignment}
        onDeclineOrder={() => setIsModalOnHoldOpen(true)}
        onSetupCompanyRelations={() => navigate(`/companies/company-details/${consignmentDetailsResponse.order.company && consignmentDetailsResponse.order.company.id}`)}
        onRecheckCredit={handleRecheckCredit}
        onOverrideCredit={handleConsignmentOnHoldtoProcessV2}
        onRaiseCredit={() => navigate(`/companies/company-details/${consignmentDetailsResponse.order.company && consignmentDetailsResponse.order.company.id}`)}
        companyId={consignmentDetailsResponse.order.companyId}
        isReadOnly={consignmentDetailsResponse.readOnly || false}
        isRecheckLoading={recheckCreditReq.isLoading}
        isOverrideLoading={overrideCreditTransactionReq.isLoading}
      />
    ) : null;

  const manufacturingDelayedButtons = (
    <FlexboxContainer
      flexDirection='row'
      justifyContent='flex-end'
    >
      {
        (consignmentDetailsResponse.previousState !== READY_FOR_PICKUP && consignmentDetailsResponse.previousState !== READY_TO_SHIP) &&
        <Button
          isGhost
          borderColor={Shade.shade550}
          color={Background.navy}
          onClick={onConfirmManufacturingClick}
          margin="0 10px 0 0"
          permissionCode={PermissionCode.MANUFACTURING_DELAY_CONFIRM}
        >
          {
            isShipping ? 'Update shipping info' : 'Update pickup info'
          }
        </Button>
      }

      <Button
        borderColor={Background.navy}
        bgColor={Background.navy}
        onClick={onAcceptDelayClick}
        permissionCode={PermissionCode.MANUFACTURING_DELAY_CONFIRM}
      >
        Confirm delayed order acceptance
      </Button>
    </FlexboxContainer>
  );

  const delayedActions = consignmentDetailsResponse.state === DELAYED
    ? (
      <Banner
        bgColor={Main.warning}
        title='Waiting for the customer to accept order delay'
        text='This order still within a 24-hour window for the buyer to cancel without any penalty.'
        text1={isShipping
          ? <>New estimated ship date: <strong>{formatDateTime(consignmentDetailsResponse.availableDate)}</strong></>
          : <>Estimated delivery for pickup: <strong>{formatDateTime(consignmentDetailsResponse.availableDate)}</strong></>
        }
        text2={isRental && consignmentDetailsResponse?.oldRentalStartDate && consignmentDetailsResponse?.oldRentalEndDate
          ? <>Previous rent dates: <strong>{formatDateTime(consignmentDetailsResponse.oldRentalStartDate)}</strong> - <strong>{formatDateTime(consignmentDetailsResponse.oldRentalEndDate)}</strong></>
          : ''
        }
        text3={
          isRental && consignmentDetailsResponse?.rentalStartDate && consignmentDetailsResponse?.rentalEndDate
            ? <>{(consignmentDetailsResponse?.oldRentalStartDate && consignmentDetailsResponse?.oldRentalEndDate) ? 'New rent dates:' : 'Rent dates:'} <strong>{formatDateTime(consignmentDetailsResponse.rentalStartDate)}</strong> - <strong>{formatDateTime(consignmentDetailsResponse.rentalEndDate)}</strong></>
            : ''
        }
        buttons={manufacturingDelayedButtons}
      />
    ) : null;

  const orderCancelingActions = (
    ([CANCELING, CANCELED].includes(consignmentDetailsResponse.state) &&
    consignmentDetailsResponse.order.customer.dowJonesStatus !== DOW_JONES_REVIEWED_BLOCKED &&
    (!isVendor && consignmentDetailsResponse.state === CANCELING && consignmentDetailsResponse.cancellationReason) ||
    (consignmentDetailsResponse.state === CANCELED && consignmentDetailsResponse.order.customer.dowJonesStatus !== DOW_JONES_REVIEWED_BLOCKED) ||
    (!isVendor && consignmentDetailsResponse.state === CANCELING && consignmentDetailsResponse.cancelationReason)) || 
    (consignmentDetailsResponse.consignmentType === CONSIGNMENT_TYPES.SUBSCRIPTION &&
     consignmentDetailsResponse.paymentStatus === PAYMENT_STATUS.FAILED &&
     consignmentDetailsResponse.state === CANCELED)
    )
    ? (
      <Banner
        titleColor={Background.white}
        bgColor={Main.error}
        title={renderCancelingMessage(consignmentDetailsResponse?.cancelationReason?.reasonCode)}
        text={renderCancelingText(consignmentDetailsResponse?.cancelationReason)}
        textAreaMarginBottom="0"
        color={Background.white}
      />
    )
    : null;

  const handleCancellationEmailConfirm = () => {
    cancellationOrderEmailReq.run(consignmentDetailsResponse.order.id);
  };

  const cancellationOrderEmailModal = (
    <OrderCancellationEmailModal
      show={cancellationEmailPopup}
      onClose={handleCloseCancellationEmailPopup}
      onConfirm={handleCancellationEmailConfirm} />
  );

  const modifyRentalDatesModal = (
    <ModifyEstDateWithRentaModal
      show={showModal}
      onClose={handleRefresh}
      onConfirm={handleSubmit}
      title={TEXT_LABELS.RENTAL_UPDATE}
      disabled={isDisabled}
      shippingCarriers={shippingCarriers}
      isShipping={consignmentDetailsResponse.deliveryType === 'SHIPPING'}
      carrier={shippingConsignmentRequest.shippingCarrier ? shippingConsignmentRequest.shippingCarrier.name : ''}
      setCarrier={onShippingCarrierSelect}
      trackingId={consignmentDetailsResponse.deliveryType === 'SHIPPING' ? shippingConsignmentRequest.trackingId : pickupConsignmentRequest.referenceNumber}
      setTrackingId={consignmentDetailsResponse.deliveryType === 'SHIPPING' ? onTrackingIdChange : onReferenceIdChange}
      estDate={consignmentDetailsResponse.deliveryType === 'SHIPPING' ? shippingConsignmentRequest.deliveryDate : pickupConsignmentRequest.pickupDate}
      setEstDate={consignmentDetailsResponse.deliveryType === 'SHIPPING' ? onDeliveryDateChange : onPickupDateChange}
      weekdaysOnly={consignmentDetailsResponse.weekdaysOnly!}
    />
  );

  const dowJonesBannerButton = (
    <Button
      borderColor={consignmentDetailsResponse.order.cancelEmailSentDate ? Main.error : Background.white}
      bgColor={consignmentDetailsResponse.order.cancelEmailSentDate ? Main.error : Background.white}
      color={consignmentDetailsResponse.order.cancelEmailSentDate ? Text.whitePrimary : `${Text.primary}`}
      onClick={handleShowCancellationEmailPopup}
      leftIcon={(size) => <Email style={{ fontSize: size }} />}
      disabled={!!consignmentDetailsResponse.order.cancelEmailSentDate || consignmentDetailsResponse.state !== CANCELED}
    >
      {
        consignmentDetailsResponse.order.cancelEmailSentDate
          ? `Cancellation email sent at ${formatDateTime(consignmentDetailsResponse.order.cancelEmailSentDate, true)}`
          : 'Send Cancelation email to the customer'
      }
    </Button>
  );

  const dowJonesBanner = consignmentDetailsResponse.state === CANCELED && consignmentDetailsResponse.order.customer.dowJonesStatus === DOW_JONES_BLOCKED || consignmentDetailsResponse.order.customer.dowJonesStatus === DOW_JONES_REVIEWED_BLOCKED
    ? (
      <Banner
        bgColor={Main.error}
        title="This customer has failed OFMP compliance check"
        text="Order is canceled"
        color={Background.white}
        titleColor={Background.white}
        buttons={dowJonesBannerButton}
        textAreaMarginBottom="0"
        titleMarginBottom={Space.spBasePlus}
      />
    ) : null;

  const orderReadyActions = consignmentDetailsResponse.state === CHECKING_CREDIT && consignmentDetailsResponse.order.customer.dowJonesStatus !== DOW_JONES_BLOCKED
    ? (
      <BannerWraper>
        <Banner
          margin='0'
          bgColor={Background.white}
          title='Credit check is currently in progress'
          text="Please wait, this can take up to 1 minute. Upon completion of the credit check, you will be able to start the order processing."
          textAreaMarginBottom="0"
          titleMarginBottom={Space.spBasePlus}
        />
      </BannerWraper>
    ) : null;

  const orderSplittingBanner = consignmentDetailsResponse.state === SPLIT_CONSIGNMENT ? splitBanner : null;

  const isManufacturingDatePassed = consignmentDetailsResponse.state === MANUFACTURING && new Date().setUTCHours(0, 0, 0, 0) > new Date(consignmentDetailsResponse.availableDate).setUTCHours(0, 0, 0, 0);

  const orderManufacturingButtons = isManufacturingDatePassed
    ? (
      <FlexboxContainer
        flexDirection='row'
        justifyContent='flex-end'
      >
        <Button
          borderColor={Background.navy}
          bgColor={Background.navy}
          onClick={onConfirmManufacturingClick}
          permissionCode={PermissionCode.MANUFACTURING_DELAY_NEW_DATE}
        >
          Provide new date / Cancel order
        </Button>
      </FlexboxContainer>
    )
    : (
      <FlexboxContainer
        flexDirection='row'
        justifyContent='flex-end'
      >
        <Button
          isGhost
          borderColor={isManufacturingDatePassed ? Shade.shade550 : Main.accent}
          color={isManufacturingDatePassed ? Background.navy : Main.accent}
          onClick={onConfirmManufacturingClick}
          margin="0 4px 0 0"
          permissionCode={PermissionCode.MANUFACTURING}
          disabled={isSalesOrderInProgress}
        >
          Provide new date / Cancel order
        </Button>
        <Button
          onClick={() => onConfirmManufacture(null, PACKING)}
          permissionCode={PermissionCode.MANUFACTURING}
          disabled={isSalesOrderInProgress}
        >
          Confirm manufacturing complete
        </Button>
      </FlexboxContainer>
    );

  const orderManufacturingActionsPayment = (
    <Banner
      loadSpinner={true}
      title='Processing payment'
      boxShadow='0px 8px 16px rgba(47, 66, 83, 0.25), 0px 16px 20px rgba(47, 66, 83, 0.15)'
      text='Please wait, this can take up to a minute.'
      bgColor={Background.white}
      spinnerColor='black'
    />
  );

  const orderManufacturingActions = (
    <Banner
      boxShadow={isManufacturingDatePassed ? '' : '0px 8px 16px rgba(47, 66, 83, 0.25), 0px 16px 20px rgba(47, 66, 83, 0.15)'}
      bgColor={isManufacturingDatePassed ? Main.warning : Background.white}
      title={
        isManufacturingDatePassed
          ? isShipping
            ? 'Manufacturing delay: estimated ship date has passed'
            : 'Manufacturing delay: estimated ready for pickup date has passed'
          : 'Manufacturing'
      }
      text={
        isShipping
          ? `Estimated ship date ${formatDateTime(shippingConsignmentRequest.deliveryDate)}`
          : `Estimated ready for pickup ${formatDateTime(pickupConsignmentRequest.pickupDate)}`
      }
    >
      <FlexboxContainer width='100%' justifyContent='space-between' alignItems='flex-end'>
        {isRental ? <RentalDates consignmentData={consignmentDetailsResponse} datesFormat='yyyy MMM dd' margin='0px' /> : <div></div>}
        {orderManufacturingButtons}
      </FlexboxContainer>
    </Banner>
  );

  const getManufacturingBanner = () => {
    if (isSubscriptionProcessingUnpaid || isAffiliateProcessingUnpaid) return orderManufacturingActionsPayment
    if (consignmentDetailsResponse?.state === MANUFACTURING && ![AWAITS_SHIPPING, CHECKING_CREDIT].includes(consignmentDetailsResponse.previousState))
      if (consignmentDetailsResponse.order.paymentType !== 'PURCHASE_ORDER' &&
        consignmentDetailsResponse.paymentStatus === 'NOT_PAID' && !isRental) return orderManufacturingActionsPayment
      else return orderManufacturingActions
    return null;
  }

  const paymentBox = (
    <FlexboxContainer width="35%" justifyContent="flex-end" height="170px">
      <ConsignmentShortPaymentsBanner
        show={showConsignmentShortPaymentsBanner}
        consignment={consignmentDetailsResponse}
        reload={() => consignmentReq.reload()} />
    </FlexboxContainer>
  );

  const orderFulfilled = isFulfillmentCompleteStep
    ? (
      <Banner
        color={Background.white}
        titleColor={Background.white}
        bgColor={Main.success}
        title={isRental ? TEXT_LABELS.RENTAL_SHIPPED_MSG : TEXT_LABELS.PURCHASE_SHIPPED_MSG}
        text={isShipping ? 'For shipping tracking please use carrier services.' : ''}
        paymentBox={showConsignmentShortPaymentsBanner ? paymentBox : null}
        titleMarginBottom={Space.spBasePlus}
        textAreaMarginBottom={Space.spBasePlus}
      >
        <FlexboxContainer width='100%' justifyContent='space-between' alignItems="flex-start">
          <FlexboxContainer width='50%' flexWrap='wrap'>
            <OrderInformationTable
              color={Background.white}
              padding="0"
              rows={[
                {
                  name: isShipping ? 'Carrier' : 'Reference #',
                  value: isShipping
                    ? shippingConsignmentRequest.shippingCarrier?.name || ''
                    : pickupConsignmentRequest.referenceNumber,
                },
                {
                  name: isShipping ? 'Tracking #' : 'Ready for pickup',
                  value: isShipping
                    ? shippingConsignmentRequest.trackingId
                    : formatDateTime(pickupConsignmentRequest.pickupDate),
                },
                {
                  name: isShipping ? 'Dispatched' : 'Collection date',
                  value: formatDateTime(dispatchRequest.dispatchDate),
                },
                {
                  name: isShipping ? 'Est. ship date' : 'Recipient name',
                  value: isShipping
                    ? formatDateTime(shippingConsignmentRequest.deliveryDate)
                    : dispatchRequest.pickedUpBy,
                },
              ]}
            />
          </FlexboxContainer>
          {isRental ? <FlexboxContainer width='50%' flexWrap='wrap' margin='38px 0px 0px 0px'>
            <OrderInformationTable
              color={Background.white}
              padding="0"
              rows={[
                {
                  name: 'Start Date',
                  value: formatDateTime(consignmentDetailsResponse.rentalStartDate),
                },
                {
                  name: 'End Date',
                  value: formatDateTime(consignmentDetailsResponse.rentalEndDate),
                },
              ]}
            />

          </FlexboxContainer> : ''}
        </FlexboxContainer>
      </Banner>
    ) : null

  const updateOrderInfoModal = (
    <Modal
      width='700px'
      left="30vw"
      showModal={openUpdateOrderModel}
      onClose={
        handleUpdateOrderInfoModalClose
      }
      onClick={onConfirmOrderInfoUpdate}
      title={
        isShipping ? 'Update shipping info' : 'Update pickup info'
      }
      disabled={(isShipping && !shippingConsignmentRequest.trackingId) || (!isShipping && !pickupConsignmentRequest.referenceNumber)}
    >
      {
        isShipping
          ? <>
            <FlexboxContainer
              flexDirection="row"
              width="100%"
              alignItems="flex-end"
              justifyContent="center"
            >
              <FlexboxContainer width="160px" margin={`0 ${Space.spHalf} 0 0`}>
                <Select
                  noBoxShadow
                  options={shippingCarriers.map(shCarrier => ({ value: shCarrier.name }))}
                  value={shippingConsignmentRequest.shippingCarrier ? shippingConsignmentRequest.shippingCarrier.name : ''}
                  defaultValue="Select carrier from the list"
                  onChange={onShippingCarrierSelect}
                  labelPosition='top'
                  label="Carrier"
                  width="100%"
                  height="36px"
                />
              </FlexboxContainer>
              <Input
                id="tracking-id"
                labelName="Tracking #"
                placeholder="Enter tracking number"
                value={shippingConsignmentRequest.trackingId}
                onChange={e => onTrackingIdChange(e.target.value)}
                width="224px"
                maxLength="50"
              />
              <FlexboxContainer flexDirection="column" width="150px" margin={`0 0 0 ${Space.spHalf}`}>
                <LabelText>Est. ship date</LabelText>
                <DatePicker
                  value={
                    shippingConsignmentRequest.deliveryDate
                      ? DateTime.fromISO(shippingConsignmentRequest.deliveryDate)
                      : null
                  }
                  onChange={(value: DateTime | null) => {
                    if (!value) return;
                    const isoDate = value.toISO() || '';
                    if (isRental) {
                      const { newRentalStartDate, newRentalEndDate } = recalculateRentalDates(value.toJSDate(), consignmentDetailsResponse.rentalStartDate, consignmentDetailsResponse.rentalEndDate)
                      onDeliveryDateChange(isoDate, newRentalStartDate, newRentalEndDate)
                    } else {
                      onDeliveryDateChange(isoDate)
                    }
                  }}
                  open={openEstDeliveryPicker}
                  onOpen={() => setOpenEstDeliveryPicker(true)}
                  onClose={() => setOpenEstDeliveryPicker(false)}
                  format="yyyy MMM dd"
                  slotProps={{
                    textField: {
                      size: 'small',
                      variant: 'outlined',
                      onClick: () => setOpenEstDeliveryPicker(true),
                      InputProps: {
                        readOnly: true,
                        sx: { cursor: 'pointer' }
                      }
                    }
                  }}
                />
              </FlexboxContainer>
            </FlexboxContainer>
            {
              isRental && <RentalNewDates consignmentData={consignmentDetailsResponse} shippingConsignmentRequest={shippingConsignmentRequest} />
            }
          </> : (<>

            <FlexboxContainer
              flexDirection='row'
              width='100%'
            >
              <FlexboxContainer width="100%" justifyContent="space-between" alignItems="center" padding="5px 0">
                <Input
                  id="reference-id"
                  labelName="Reference #"
                  placeholder="Enter reference number"
                  value={pickupConsignmentRequest.referenceNumber}
                  onChange={e => onReferenceIdChange(e.target.value)}
                  width="50%"
                  maxLength="50"
                />
                <FlexboxContainer flexDirection="column" width="50%" margin={`0 0 0 ${Space.spHalf}`}>
                  <LabelText>Ready for pickup</LabelText>
                  <DatePicker
                    value={
                      pickupConsignmentRequest.pickupDate
                        ? DateTime.fromISO(pickupConsignmentRequest.pickupDate)
                        : null
                    }
                    onChange={(value: DateTime | null) => {
                      if (!value) return;
                      const isoDate = value.toISO() || '';
                      if (isRental) {
                        const { newRentalStartDate, newRentalEndDate } = recalculateRentalDates(value.toJSDate(), consignmentDetailsResponse.rentalStartDate, consignmentDetailsResponse.rentalEndDate)
                        onPickupDateChange(isoDate, newRentalStartDate, newRentalEndDate)
                      } else {
                        onPickupDateChange(isoDate)
                      }
                    }}
                    open={openPickupDatePicker}
                    onOpen={() => setOpenPickupDatePicker(true)}
                    onClose={() => setOpenPickupDatePicker(false)}
                    format="yyyy MMM dd"
                    slotProps={{
                      textField: {
                        size: 'small',
                        variant: 'outlined',
                        onClick: () => setOpenPickupDatePicker(true),
                        InputProps: {
                          readOnly: true,
                          sx: { cursor: 'pointer' }
                        }
                      }
                    }}
                  />
                </FlexboxContainer>
              </FlexboxContainer>


            </FlexboxContainer>
          </>
          )
      }
    </Modal>
  );

  const awaitingDispatchActions = isAwaitingDispatchStep
    ? (
      <Banner
        boxShadow='0px 8px 16px rgba(47, 66, 83, 0.25), 0px 16px 20px rgba(47, 66, 83, 0.15)'
        bgColor={Background.white}
        title={isShipping ? 'Awaiting order dispatch' : 'Order is ready and awaits pickup'}
        text={loader ? TEXT_LABELS.PLEASE_WAIT : ''}
        loadSpinner={loader}
      >
        {
          !loader &&
          <>
            <ConfirmOrder
              onClick={() => consignmentDetailsResponse.consignmentType === CONSIGNMENT_TYPES.PURCHASE ? setOpenUpdateOrderModal(true) : setShowModal(true)}
              rowName1={isShipping ? 'Carrier' : 'Reference #'}
              rowName2={isShipping ? 'Tracking #' : 'Ready for pickup'}
              rowValue1={
                isShipping
                  ? shippingConsignmentRequest.shippingCarrier
                    ? shippingConsignmentRequest.shippingCarrier.name
                    : ''
                  : pickupConsignmentRequest.referenceNumber
              }
              rowValue2={
                isShipping
                  ? shippingConsignmentRequest.trackingId
                  : pickupConsignmentRequest.pickupDate
                    ? formatDateTime(pickupConsignmentRequest.pickupDate)
                    : ''
              }
              rowName3={
                isShipping
                  ? 'Est. ship date'
                  : ''
              }
              rowValue3={
                isShipping
                  ? formatDateTime(shippingConsignmentRequest.deliveryDate)
                  : ''
              }
              permsissionCode={isShipping ? PermissionCode.READY_SHIPPING_DETAILS : PermissionCode.PICKED_UP_DETAILS_MANAGEMENT}
            />
            <FlexboxContainer width='100%' justifyContent='space-between' >
              <FlexboxContainer width='80%'>
                <FlexboxContainer
                  flexDirection="column"
                  width="150px"
                  margin='0 4px 0 0'
                >
                  <LabelText>
                    {
                      isShipping ? 'Dispatch date' : 'Collection date'
                    }
                  </LabelText>
                  <ReadOnlyWrapper readOnly={isShipping ? isShippingDispatchReadOnly : isPickupDispatchReadOnly}>
                    <DatePicker
                      value={dispatchRequest.dispatchDate ? DateTime.fromISO(dispatchRequest.dispatchDate) : null}
                      onChange={(value: DateTime | null) => {
                        if (value) {
                          onDispatchDateChange(value.toISO() || '');
                        }
                      }}
                      open={openDispatchDatePicker}
                      onOpen={() => setOpenDispatchDatePicker(true)}
                      onClose={() => setOpenDispatchDatePicker(false)}
                      format="yyyy MMM dd"
                      slotProps={{
                        textField: {
                          size: 'small',
                          onClick: () => {
                            const isDisabled = (isShipping ? isShippingDispatchReadOnly : isPickupDispatchReadOnly) || isFulfillmentCompleteStep;
                            if (!isDisabled) setOpenDispatchDatePicker(true);
                          },
                          InputProps: {
                            readOnly: true,
                            sx: { cursor: ((isShipping ? isShippingDispatchReadOnly : isPickupDispatchReadOnly) || isFulfillmentCompleteStep) ? 'default' : 'pointer' },
                          }
                        }
                      }}
                      disabled={(isShipping ? isShippingDispatchReadOnly : isPickupDispatchReadOnly) || isFulfillmentCompleteStep}
                    />
                  </ReadOnlyWrapper>
                </FlexboxContainer>
                {
                  !isShipping
                    ? (
                      <Input
                        id="recipient-name-id"
                        labelName="Recipient name"
                        placeholder="Enter recipent name"
                        value={dispatchRequest.pickedUpBy}
                        onChange={e => onPickedUpByChange(e.target.value)}
                        width="272px"
                        disabled={isFulfillmentCompleteStep}
                        readOnly={isPickupDispatchReadOnly}
                      />
                    ) : null
                }
              </FlexboxContainer>
              <FlexboxContainer
                justifyContent="flex-end"
                alignItems="flex-end"
                width="100%"
              >

                <Button
                  onClick={isFulfillmentCompleteStep ? onViewPaymentStatus : onConfirmDispatchClick}
                  bgColor={Main.brandDark}
                  borderColor={Main.brandDark}
                  permissionCode={isShipping ? PermissionCode.READY_DISPATCH_DATE : PermissionCode.PICKED_UP_DETAILS_MANAGEMENT}
                >
                  {
                    isFulfillmentCompleteStep
                      ? 'View status of my payment'
                      : isShipping
                        ? 'Confirm order dispatch'
                        : 'Confirm order pickup'
                  }
                </Button>
              </FlexboxContainer>
            </FlexboxContainer>
          </>
        }
        <FlexboxContainer>
          {isRental ? <RentalDates consignmentData={consignmentDetailsResponse} datesFormat='yyyy MMM dd' isRental={isRental} /> : ''}
        </FlexboxContainer>
      </Banner>
    ) : null;

  const rentalSteps = isRentalStep ?
    <RentalOrderStates isVendor={isVendor} consignmentData={consignmentDetailsResponse} paymentStatus={consignmentDetailsResponse.paymentStatus} refreshConsignment={refreshConsignment} shippingCarriers={shippingCarriers} /> : null;

  const subscriptionSteps = isSubscription && [SUBSCRIPTION_ACTIVE, SUBSCRIPTION_RENEWAL].includes(consignmentDetailsResponse.state)
    ? <SubscriptionOrderStates consignmentData={consignmentDetailsResponse} refreshConsignment={refreshConsignment} />
    : null;

  const affiliateSteps = isAffiliateProgram ?
    <AffiliateOrderStates consignmentData={consignmentDetailsResponse} documents={documents} refreshConsignment={refreshConsignment} /> : null;

  const disableSerialNumber = (consignmentDetailsResponse.state === NEW && consignmentDetailsResponse.previousState === NEW)
    || ([NEW, READY, ORDER_SENT_TO_OEM, CHECKING_CREDIT].includes(consignmentDetailsResponse.state) && consignmentDetailsResponse.order.customer.dowJonesStatus === DOW_JONES_BLOCKED)
    || (consignmentDetailsResponse.state === AWAITS_SHIPPING_APPROVAL)
    || (consignmentDetailsResponse.state === PACKING && !isPreparationStep)
    || (consignmentDetailsResponse.state === MODIFIED_BY_VENDOR && consignmentDetailsResponse.previousState !== MODIFIED_BY_VENDOR)
    || (consignmentDetailsResponse.state === RENT_COMPLETED)
    || (consignmentDetailsResponse.state === CANCELED)
    || (consignmentDetailsResponse.state === CANCELING);

  const deliveryOrder = isShipping
    ? (
      <FlexboxContainer flexDirection="column">
        <ShippingCostRequiredItems
          deliveryDate={consignmentDetailsResponse.availableDate}
          subTotal={consignmentDetailsResponse.subTotal}
          consignmentEntries={consignmentDetailsResponse.consignmentEntries}
          consignmentEntriesAddons={consignmentDetailsResponse.consignmentEntriesAddons}
          isPreparationStep={isPreparationStep}
          totalTax={consignmentDetailsResponse.totalTax}
          totalSurcharges={consignmentDetailsResponse.totalSurcharges}
          totalDeliveryCost={consignmentDetailsResponse.totalDeliveryCost}
          totalPriceWithTax={consignmentDetailsResponse.totalPriceWithTax}
          isRental={isRental}
          handleSerialNumberUpdate={handleSerialNumberUpdate}
          serialNumbers={serialNumbers}
          setSerialNumbers={setSerialNumbers}
          canEditSerialNumber={!disableSerialNumber}
          countryId={consignmentDetailsResponse.countryId}
          totalDiscounts={consignmentDetailsResponse.totalDiscounts}
        />
      </FlexboxContainer>
    ) : null;

  const shippingDetailsButton = consignmentDetailsResponse.state === PACKING
    ? (
      <Button
        onClick={onContinueClick}
        disabled={isDeliveryButtonDisabled || isSalesOrderInProgress}
        permissionCode={PermissionCode.PREPARING_SHIPPING_DETAILS}
      >
        Confirm order ready to ship
      </Button>
    ) : (
      <Button
        onClick={onContinueClick}
        disabled={isDeliveryButtonDisabled}
        permissionCode={PermissionCode.PREPARING_SHIPPING_DETAILS}
      >
        Update shipping information
      </Button>
    );

  const pickupDetailsButton = consignmentDetailsResponse.state === PACKING ? (
    <Button
      onClick={onContinueClick}
      disabled={isPickupButtonDisabled || isSalesOrderInProgress}
      permissionCode={PermissionCode.PICK_UP_DETAILS_MANAGEMENT}
    >
      Confirm order ready to pickup
    </Button>
  ) : (
    <Button
      onClick={onContinueClick}
      disabled={isPickupButtonDisabled}
      permissionCode={PermissionCode.PICK_UP_DETAILS_MANAGEMENT}
    >
      Update pickup information
    </Button>
  )

  const pickupShippingDetails = (consignmentDetailsResponse.order.paymentType !== 'PURCHASE_ORDER' && consignmentDetailsResponse.paymentStatus === 'NOT_PAID' && consignmentDetailsResponse.consignmentType === 'PURCHASE')
    ? (
      <Banner
        loadSpinner={true}
        title='Processing payment'
        boxShadow='0px 8px 16px rgba(47, 66, 83, 0.25), 0px 16px 20px rgba(47, 66, 83, 0.15)'
        text='Please wait, this can take up to a minute.'
        bgColor={Background.white}
      />
    ) : isShipping ? (
      <Banner
        title='Preparing for shipment'
        text={loader ? TEXT_LABELS.PLEASE_WAIT : 'Shipping details below will be shared with the customer on submission.'}
        bgColor={Background.white}
        loadSpinner={loader}
        boxShadow='0px 8px 16px rgba(47, 66, 83, 0.25), 0px 16px 20px rgba(47, 66, 83, 0.15)'
      >
        {
          !loader &&
          <ShippingDetails
            shippingCarriers={shippingCarriers.map(shCarrier => ({ value: shCarrier.name }))}
            shippingCarrierValue={shippingConsignmentRequest.shippingCarrier ? shippingConsignmentRequest.shippingCarrier.name : ''}
            onChange={onShippingCarrierSelect}
            trackingId={shippingConsignmentRequest.trackingId}
            onTrackingIdChange={e => onTrackingIdChange(e.target.value)}
            deliveryDate={shippingConsignmentRequest.deliveryDate || null}
            onDeliveryDateChange={onDeliveryDateChange}
            button={shippingDetailsButton}
            isRental={isRental}
            consignmentData={consignmentDetailsResponse}
            setShowModal={setShowModal}
          />
        }
      </Banner>
    ) : (
      <Banner
        title='Preparing for pickup'
        boxShadow='0px 8px 16px rgba(47, 66, 83, 0.25), 0px 16px 20px rgba(47, 66, 83, 0.15)'
        text={loader ? TEXT_LABELS.PLEASE_WAIT : 'Pickup details below will be shared with the customer on submission.'}
        bgColor={Background.white}
        loadSpinner={loader}
      >
        {
          !loader &&
          <PickupDetails
            warehouseName={consignmentDetailsResponse.warehouse.name}
            referenceNumber={pickupConsignmentRequest.referenceNumber}
            consignmentId={consignmentDetailsResponse.consignmentId}
            pickupDate={pickupConsignmentRequest.pickupDate || null}
            onReferenceIdChange={e => onReferenceIdChange(e.target.value)}
            onPickupDateChange={onPickupDateChange}
            button={pickupDetailsButton}
            isRental={isRental}
            consignmentData={consignmentDetailsResponse}
            setShowModal={setShowModal}
          />
        }
      </Banner>
    );

  const pickupOrder = !isShipping
    ? (
      <FlexboxContainer flexDirection="column">
        <ShippingCostRequiredItems
          deliveryDate={consignmentDetailsResponse.availableDate}
          subTotal={consignmentDetailsResponse.subTotal}
          consignmentEntries={consignmentDetailsResponse.consignmentEntries}
          consignmentEntriesAddons={consignmentDetailsResponse.consignmentEntriesAddons}
          isPreparationStep={isPreparationStep}
          totalTax={consignmentDetailsResponse.totalTax}
          totalSurcharges={consignmentDetailsResponse.totalSurcharges}
          totalPriceWithTax={consignmentDetailsResponse.totalPriceWithTax}
          isRental={isRental}
          handleSerialNumberUpdate={handleSerialNumberUpdate}
          serialNumbers={serialNumbers}
          setSerialNumbers={setSerialNumbers}
          canEditSerialNumber={!disableSerialNumber}
          countryId={consignmentDetailsResponse.countryId}
          totalDiscounts={consignmentDetailsResponse.totalDiscounts}
        />
      </FlexboxContainer>
    ) : null;

  const orderPreparationBanner = isPreparationStep
    ? (
      <FlexboxContainer margin='20px 0' flexDirection='column'>
        {
          pickupShippingDetails
        }
      </FlexboxContainer>
    ) : null;

  const cancellationBanner = !isSubscription && (consignmentDetailsResponse.state === CANCELATION_FAILED || consignmentDetailsResponse.state === CANCELATION_APPROVAL) && consignmentDetailsResponse?.salesOrderNumber && !isFulfillmentStep
    ? (
      <FlexboxContainer margin='20px 0'>
        <SalesOrderCancellationBanner
          show={true}
          consignmentState={consignmentDetailsResponse.state}
          onRetry={handleRetryCancellation}
          onReject={handleRejectCancellation}
          onApprove={handleApproveCancellation}
        />
      </FlexboxContainer>
    ) : null;

  const salesOrderComponent = !isSubscription && user &&
    <SalesOrder
      isAdmin={User.userIsAllowed(user, [User.Role.Admin, User.Role.SuperAdmin, User.Role.VendorAdmin])}
      buttonClick={setSalesOrderModalOpen}
      openSalesOrderDetails={setSalesOrderDetailsModalOpen}
      salesOrderId={consignmentDetailsResponse.salesOrderNumber}
      orderRejectionStatus={salesOrderResult[0]?.orderRejectionStatus}
      deliveryStatus={salesOrderResult[0]?.deliveryStatus}
      isTimeout={salesOrderTimeout}
      orderType={getSalesOrderType(consignmentDetailsResponse.consignmentEntries, isManualConfirmed, consignmentDetailsResponse.isStockManual || false, consignmentDetailsResponse.businessUnitCompany?.creditDetails?.source, consignmentDetailsResponse.order.paymentType, consignmentDetailsResponse.salesOrderNumber)}
      salesOrderInProgress={isSalesOrderInProgress}
    />

  const orderPreparation = isPreparationStep
    ? (
      <FlexboxContainer
        flexDirection="column"
        margin="0 0 10px 0"
      >
        <Content>
          {
            isShipping ? deliveryOrder : pickupOrder
          }
        </Content>
        <Documents
          orderNumber={consignmentDetailsResponse.order.orderId}
          customerEmail={consignmentDetailsResponse.order.customer.email}
          consignmentId={consignmentDetailsResponse.consignmentId.slice(4)}
          vendorId={consignmentDetailsResponse.vendorId}
          documents={documents}
          orderDocuments={orderDocuments}
        />
      </FlexboxContainer>
    ) : null;

  const renderOrderDetailsTitle = (heading: string, padding = '') => {
    return (
      <FlexboxContainer padding={padding ? padding : '0px 16px 0px 13px'} justifyContent="space-between" alignItems="center">
        <SubHeading>{heading}</SubHeading>
      </FlexboxContainer>
    );
  };

  const orderProducts = !isPreparationStep && !isFulfillmentStep
    ? (
      <>
        {
          consignmentDetailsResponse.consignmentEntries.length > 0
            ? (
              <Section>
                {renderOrderDetailsTitle('Order items')}
                <ShippingCostRequiredItems
                  deliveryDate={consignmentDetailsResponse.availableDate}
                  subTotal={consignmentDetailsResponse.subTotal}
                  consignmentEntries={consignmentDetailsResponse.consignmentEntries}
                  consignmentEntriesAddons={consignmentDetailsResponse.consignmentEntriesAddons}
                  totalTax={consignmentDetailsResponse.totalTax}
                  totalSurcharges={consignmentDetailsResponse.totalSurcharges}
                  totalDeliveryCost={consignmentDetailsResponse.totalDeliveryCost}
                  totalPriceWithTax={consignmentDetailsResponse.totalPriceWithTax}
                  state={consignmentDetailsResponse.state}
                  isRental={isRental}
                  handleSerialNumberUpdate={handleSerialNumberUpdate}
                  serialNumbers={serialNumbers}
                  setSerialNumbers={setSerialNumbers}
                  canEditSerialNumber={!disableSerialNumber}
                  countryId={consignmentDetailsResponse.countryId}
                  totalDiscounts={consignmentDetailsResponse.totalDiscounts}
                />
              </Section>
            ) : null
        }
        <Documents
          orderNumber={consignmentDetailsResponse.order.orderId}
          customerEmail={consignmentDetailsResponse.order.customer.email}
          consignmentId={consignmentDetailsResponse.consignmentId.slice(4)}
          vendorId={consignmentDetailsResponse.vendorId}
          documents={documents}
          orderDocuments={orderDocuments}
        />
      </>
    ) : null;


  // Sort splitted consignments by state in descending order
  splitConsignments.sort((a: any, b: any) => {
    const stateOrder = [CANCELED, CANCELING, MANUFACTURING, PROCESSING];
    return stateOrder.indexOf(b.state) - stateOrder.indexOf(a.state);
  });

  const postOrderShippingCostSplitForm = isPostOrderShippingCostSplitFormOpen
    ? (
      <>
        <FlexboxContainer margin="16px 0 20px 16px">
          <Span style={{ fontSize: '16px', lineHeight: '24px' }}>
            Provide shipping cost for the items below. Recalculated order total will be sent to the customer for approval after credit check is performed.
          </Span>
        </FlexboxContainer>
        <Banner
          bgColor={Background.white}
          boxShadow={'0px 8px 16px rgba(47, 66, 83, 0.25), 0px 16px 20px rgba(47, 66, 83, 0.15)'}
        >
          {renderOrderDetailsTitle('Distribute shipping cost', '0px')}
          {
            splitConsignments.map((cons: any) => {

              return isRental ? (
                <SplitPostOrderShippingCostRent
                  key={cons.consignmentId}
                  isInvalid={isInvalid}
                  errorMessage={errorMessage}
                  setAdditionalShippingCostsValues={setAdditionalShippingCostsValues}
                  additionalShippingCostsValues={additionalShippingCostsValues}
                  consignmentId={cons.consignmentId.slice(4)}
                  fullConsignmentId={cons.consignmentId}
                  state={cons.state}
                  deliveryCost={cons.deliveryCost}
                  deliveryDate={cons.availableDate}
                  consignmentEntries={cons.entries}
                  isRental={isRental}
                  splitConsignmentBody={splitConsignmentBody}
                  fulfillmentRequest={fulfillmentRequest}
                  country={country}
                />
              ) :
                (
                  <SplitPostOrderShippingCost
                    key={cons.consignmentId}
                    isInvalid={isInvalid}
                    errorMessage={errorMessage}
                    setAdditionalShippingCostsValues={setAdditionalShippingCostsValues}
                    additionalShippingCostsValues={additionalShippingCostsValues}
                    consignmentId={cons.consignmentId.slice(4)}
                    fullConsignmentId={cons.consignmentId}
                    state={cons.state}
                    deliveryCost={cons.deliveryCost}
                    deliveryDate={cons.availableDate}
                    consignmentEntries={cons.entries}
                    fulfillmentRequest={fulfillmentRequest}
                    country={country}

                  />
                )
            })
          }


          <FlexboxContainer margin="20px 0 20px 0" justifyContent="flex-end" alignItems="flex-end">
            <Button
              isGhost
              theme='disabled'
              color={Shade.shade550}
              margin={`0 ${Space.spHalf}`}
              onClick={handleBackFromEmulateScreen}
            >
              Back
            </Button>
            <ReadOnlyWrapper readOnly={additionalShippingCostReadOnly}>
              <Button
                onClick={isPostOrderShippingCostSplitFormOpen ? onConfirmFulfillmentClick : onConfirmSplitClick}
                disabled={isOrderFulfillmentButtonDisabled}
              >
                Confirm
              </Button>
            </ReadOnlyWrapper>
          </FlexboxContainer>
        </Banner>
      </>

    ) : null;



  const fulfillmentTable = isFulfillmentStep && !isSubscription && !isAffiliateProgram
    ? (
      <>
        <StockApiErrorBanner
          show={showStockApiError}
          onRetry={handleRetryStockApi}
          onManualConfirm={handleManualStockConfirm}
          isManualConfirmed={isManualConfirmed}
          setIsManualConfirmed={setIsManualConfirmed}
        />

        <Banner
          bgColor={Background.white}
          boxShadow={'0px 8px 16px rgba(47, 66, 83, 0.25), 0px 16px 20px rgba(47, 66, 83, 0.15)'}
        >

          <OrderFulfillmentTable
            fulfillmentRequest={fulfillmentRequest}
            setFulfillmentRequest={setFulfillmentRequest}
            renderOrderDetailsTitle={renderOrderDetailsTitle}
            deliveryMode={consignmentDetailsResponse.consignmentEntries[0].deliveryMode}
            consignmentType={consignmentDetailsResponse.consignmentType}
            consignmentDetailsResponse={consignmentDetailsResponse}
            setIsReviewPage={setIsReviewPage}
            isReviewPage={isReviewPage}
            setIsReviewPageMounted={setIsReviewPageMounted}
            weekdaysOnly={consignmentDetailsResponse.weekdaysOnly}
            onConfirm={isPostOrderShippingCostSplitFormOpen ? onConfirmFulfillmentClick : onConfirmSplitClick}
            disabled={isOrderFulfillmentButtonDisabled}
            isStockApiFailed={showStockApiError}
          />

          {!isReviewPage && <>
            <FlexboxContainer margin="20px 0 20px 0" justifyContent="flex-end" alignItems="flex-end">
              <Button
                borderColor={Main.brandDark}
                bgColor={Main.brandDark}
                onClick={isPostOrderShippingCostSplitFormOpen ? onConfirmFulfillmentClick : onConfirmSplitClick}
                disabled={isOrderFulfillmentButtonDisabled || splitConsignmentReq.isLoading}
                permissionCode={PermissionCode.SPLIT_IN_STOCK_MANUFACTURE}
              >
                {splitConsignmentReq.isLoading && <WhiteLoadingIndicator size={20} margin="0 8px 0 0" />}
                Confirm
              </Button>
            </FlexboxContainer>
          </>
          }


          {isReviewPage && isReviewPageMounted && <FlexboxContainer margin="20px 0 20px 0" justifyContent="flex-end" alignItems="flex-end">

            <Button
              isGhost
              margin={`0 ${Space.spHalf}`}
              color={Main.accent}
              onClick={() => {
                setIsReviewPage(false)
                setIsReviewPageMounted(false)
                // Scroll to top
                window.scrollTo(0, 0)
              }
              }
            >
              Back
            </Button>


            <Button
              borderColor={Main.brandDark}
              bgColor={Main.brandDark}
              onClick={isPostOrderShippingCostSplitFormOpen ? onConfirmFulfillmentClick : onConfirmSplitClick}
              disabled={isOrderFulfillmentButtonDisabled}
            >
              Confirm
            </Button>
          </FlexboxContainer>
          }
        </Banner>
      </>
    ) : null;

  const orderFulfillmentTable = isFulfillmentStep && !isSubscription
    ? (
      <FlexboxContainer
        flexDirection="column"
        margin="0 0 10px 0">
        <Documents
          orderNumber={consignmentDetailsResponse.order.orderId}
          customerEmail={consignmentDetailsResponse.order.customer.email}
          consignmentId={consignmentDetailsResponse.consignmentId.slice(4)}
          vendorId={consignmentDetailsResponse.vendorId}
          documents={documents}
          orderDocuments={orderDocuments}
        />
      </FlexboxContainer>
    ) : null;

  const {
    displayState,
    orderDetailsState
  } = getSteps(
    consignmentDetailsResponse.state,
    [consignmentDetailsResponse.consignmentEntries[0]?.deliveryMode],
    isVendor,
    consignmentDetailsResponse.order.customer.dowJonesStatus!,
    featureFlags,
    consignmentDetailsResponse.consignmentType
  );

  const pillTheme = [READY, ORDER_SENT_TO_OEM, MANUFACTURING, PROCESSING, READY_TO_SHIP, READY_FOR_PICKUP, SHIPPED_READY_FOR_PICKUP, SHIPPED, PICKED_UP, DELIVERED_PICKED_UP, PACKING, AWAITS_SHIPPING, RENT_IN_PROGRESS, RENT_COMPLETED, SUBSCRIPTION_ACTIVE, AFFILIATE_PROGRAM_ACTIVE].includes(consignmentDetailsResponse.state) && consignmentDetailsResponse.order.customer.dowJonesStatus !== DOW_JONES_BLOCKED
    ? 'success'
    : [ON_HOLD, DELAYED, RENT_RETURN_PENDING, MODIFIED_BY_CUSTOMER, MODIFIED_BY_VENDOR, RENT_UPDATED, CANCELATION_APPROVAL, SUBSCRIPTION_RENEWAL, AWAITS_EDIT_APPROVAL].includes(consignmentDetailsResponse.state) || (!isVendor && consignmentDetailsResponse.order.customer.dowJonesStatus === DOW_JONES_BLOCKED)
      ? 'warning'
      : [AWAITS_SHIPPING_APPROVAL, CHECKING_CREDIT, SPLIT_CONSIGNMENT].includes(consignmentDetailsResponse.state) || (isVendor && consignmentDetailsResponse.order.customer.dowJonesStatus === DOW_JONES_BLOCKED)
        ? Background.navy
        : 'danger';




  const pillThemeSplitting = liveUpdates ? Background.navy : (isSubscriptionProcessingUnpaid || isAffiliateProcessingUnpaid || ([MANUFACTURING, PACKING, RENT_COMPLETED].includes(consignmentDetailsResponse.state) && consignmentDetailsResponse.order.paymentType !== 'PURCHASE_ORDER' && consignmentDetailsResponse.paymentStatus === PAYMENT_STATUS.NOT_PAID && ([RENT_COMPLETED].includes(consignmentDetailsResponse.state)))) ? Background.navy : consignmentDetailsResponse.paymentStatus === PAYMENT_STATUS.FAILED && (!isRental || [RENT_COMPLETED].includes(consignmentDetailsResponse.state)) ? Background.red : pillTheme

  const pill = <Pill 
  margin="0 0 20px 16px" 
  theme={pillThemeSplitting} 
  bgColor={pillThemeSplitting} 
  size="xsPlus" 
  padding="4px">
    {liveUpdates ? 'SPLITTING' : (isSubscriptionProcessingUnpaid  || isAffiliateProcessingUnpaid
      || ([MANUFACTURING, PACKING, RENT_COMPLETED].includes(consignmentDetailsResponse.state) && consignmentDetailsResponse.order.paymentType !== 'PURCHASE_ORDER' && consignmentDetailsResponse.paymentStatus === PAYMENT_STATUS.NOT_PAID && (!isRental || [RENT_COMPLETED].includes(consignmentDetailsResponse.state)) && consignmentDetailsResponse.previousState !== AWAITS_SHIPPING))
      ? 'CHARGING' : consignmentDetailsResponse.paymentStatus === PAYMENT_STATUS.FAILED && ([RENT_COMPLETED].includes(consignmentDetailsResponse.state) || (consignmentDetailsResponse.consignmentType === CONSIGNMENT_TYPES.SUBSCRIPTION && consignmentDetailsResponse.state === SUBSCRIPTION_RENEWAL))
      ? 'PAYMENT FAILED' : orderDetailsState || displayState}
    </Pill>;


  const orderInformation = isShipping
    ? (
      <OrderDetailsInformation

        show={!isSubscription}
        showShippingInformation={true}
        customer={consignmentDetailsResponse.order.customer}
        companyName={
          consignmentDetailsResponse.order.company
            ? consignmentDetailsResponse.order.companyName
            : ''
        }
        newCompanyName={
          consignmentDetailsResponse.order.company && consignmentDetailsResponse.order.company.companyHistoryNameCount > 0 && consignmentDetailsResponse.order.companyName !== consignmentDetailsResponse.order.company.companyName
            ? consignmentDetailsResponse.order.company.companyName
            : ''
        }
        paymentMethod={consignmentDetailsResponse.order.paymentType}
        taxExempt={consignmentDetailsResponse.totalTax > 0 ? 'No' : 'Yes'}
        warehouse={consignmentDetailsResponse ? consignmentDetailsResponse.warehouse.name : ''}
        deliveryAddress={
          consignmentDetailsResponse.deliveryAddress
            ? capitalize(`${consignmentDetailsResponse.deliveryAddress.addressLine1 ? consignmentDetailsResponse.deliveryAddress.addressLine1 + ',' : ''} 
                          ${consignmentDetailsResponse.deliveryAddress.addressLine2 ? consignmentDetailsResponse.deliveryAddress.addressLine2 + ',' : ''} 
                          ${consignmentDetailsResponse.deliveryAddress.town ? consignmentDetailsResponse.deliveryAddress.town + ',' : ''} 
                          ${consignmentDetailsResponse.deliveryAddress.state ? consignmentDetailsResponse.deliveryAddress.state + ',' : ''} 
                          ${consignmentDetailsResponse.deliveryAddress.postalCode || ''}`) +
            `${consignmentDetailsResponse.deliveryAddress.countryIsoCode && ', ' + getCountryName(consignmentDetailsResponse.deliveryAddress.countryIsoCode)}`
            : ''
        }
        pickupContactDetails={consignmentDetailsResponse ? stringifyPickupContact(consignmentDetailsResponse.pickupContact) : ''}
        pricebookId={consignmentDetailsResponse.consignmentEntries[0]?.priceBook?.PricebookVendorId}
        creditDetails={consignmentDetailsResponse.businessUnitCompany?.creditDetails}
        creditTransactions={transactionCreditData}
        orderStatus={consignmentDetailsResponse.state}
        cancelationReasonCode={consignmentDetailsResponse.cancelationReason?.reasonCode}
      />
    ) : (
      <OrderDetailsInformation

        show={!isSubscription}
        showShippingInformation={false}
        customer={consignmentDetailsResponse.order.customer}
        companyName={
          consignmentDetailsResponse.order.company
            ? consignmentDetailsResponse.order.companyName
            : ''
        }
        newCompanyName={
          consignmentDetailsResponse.order.company && consignmentDetailsResponse.order.company.companyHistoryNameCount > 0 && consignmentDetailsResponse.order.companyName !== consignmentDetailsResponse.order.company.companyName
            ? consignmentDetailsResponse.order.company.companyName
            : ''
        }
        paymentMethod={consignmentDetailsResponse.order.paymentType}
        taxExempt={consignmentDetailsResponse.totalTax > 0 ? 'No' : 'Yes'}
        pickupLocation={
          consignmentDetailsResponse.consignmentEntries[0]
            ? consignmentDetailsResponse.consignmentEntries[0].pointOfServiceLocation
            : ''
        }
        warehouse={consignmentDetailsResponse ? consignmentDetailsResponse.warehouse.name : ''}
        pickupContactDetails={consignmentDetailsResponse ? stringifyPickupContact(consignmentDetailsResponse.pickupContact) : ''}
        pricebookId={consignmentDetailsResponse.consignmentEntries[0]?.priceBook?.PricebookVendorId}
        creditDetails={consignmentDetailsResponse.businessUnitCompany?.creditDetails}
        creditTransactions={transactionCreditData}
        orderStatus={consignmentDetailsResponse.state}
        cancelationReasonCode={consignmentDetailsResponse.cancelationReason?.reasonCode}
      />
    );

  const cancellationConsignmentModal = (
    <ConsignmentCancelallationModal
      show={cancellationConsignmentPopup}
      onClose={handleCloseCancellationConsignmentPopup}
      onConfirm={handleConsignmentCancellationConfirm}
      title='Canceling consignment'
      labels={['Please provide a cancelation reason which will be sent as a part of the “Cancelation notification” email to both Customer and Vendor']}
      textareaPlaceholder='Unfortunately, we are forced to cancel these items due to...'
    />
  );

  const cancellationApprovalModal = (
    <CancellationApprovalModal
      show={cancellationApprovalPopup}
      onClose={handleCloseCancellationApprovalPopup}
      onConfirm={handleCancellationApprovalConfirm}
      title={isAutomaticCancellationMode ? TEXT_LABELS.AUTO_CANCEL_SALESORDER : TEXT_LABELS.CANCELLATION_APPROVAL_TITLE}
      dropdownOptions={rejectionReasons}
      dropdownLabel={TEXT_LABELS.REJECTION_REASON_LABEL}
      dropdownPlaceholder={TEXT_LABELS.REJECTION_REASON_PLACEHOLDER}
      message1={!isAutomaticCancellationMode ? TEXT_LABELS.CANCELLATION_APPROVAL_MESSAGE_1 : ``}
      message2={!isAutomaticCancellationMode ? TEXT_LABELS.CANCELLATION_APPROVAL_MESSAGE_2 : ``}
      confirmButtonText={isAutomaticCancellationMode ? TEXT_LABELS.CANCEL_ORDER_AND_SO : TEXT_LABELS.SEND_FOR_APPROVAL}
    />
  );

  const confirmOrderModal = (
    <Modal
      showModal={isConfirmOrderModalOpen}
      onClose={onModalClose}
      onGhostButtonClick={() => onUpdatePreparationClick(false)}
      onClick={() => onUpdatePreparationClick(true)}
      title="Include all attached documents into notification email sent to the customer?"
      primaryButtonText="Include"
      ghostButtonText="Send email without documents"
    >
      <></>
    </Modal>
  );

  const orderComplianceCheck = (
    <BannerWraper>
      <Banner
        margin='0'
        title={TEXT_LABELS.ORDER_COMPLIANCE_CHECK}
        bgColor={Background.white}
        textAreaMarginBottom="0"
        titleMarginBottom={Space.spBasePlus}
        bannerHtml={
          <FlexboxContainer flexDirection='column' gap='10px'>
            {
              consignmentDetailsResponse.consignmentType === CONSIGNMENT_TYPES.RENT &&
              <WarningBanner message={TEXT_LABELS.RENT_MEASUREMENT_UNIT_INFO} fontSize='16px' />
            }
            {TEXT_LABELS.ORDER_COMPLIANCE_PROCESSING}
          </FlexboxContainer>
        }
      />
    </BannerWraper>
  )

  const dowJonesBlockedAdminButton = consignmentDetailsResponse.order.customer.dowJonesStatus === DOW_JONES_BLOCKED || consignmentDetailsResponse.state === DOW_JONES_FAILED
    ? (
      <Button
        borderColor={Main.accent}
        onClick={() => navigate(`/customers/customer-details/${consignmentDetailsResponse.order.customer.id}`, false, { activeTab: 1 })}
      >
        Review customer's compliance status
      </Button>
    )
    : null;

  const consignmentDowJonesBaner = [NEW, READY, ORDER_SENT_TO_OEM, CHECKING_CREDIT, ON_HOLD].includes(consignmentDetailsResponse.state) && consignmentDetailsResponse.order.customer.dowJonesStatus === DOW_JONES_BLOCKED
    ? (
      isVendor
        ? (
          orderComplianceCheck
        ) : (
          <>
            {
              consignmentDetailsResponse.consignmentType === CONSIGNMENT_TYPES.RENT &&
              <WarningBanner message={TEXT_LABELS.RENT_MEASUREMENT_UNIT_INFO} fontSize='16px' />
            }
            <Banner
              title='The customer has failed the Dow Jones compliance check and requires a review'
              text='Till then this order will stay on hold'
              bgColor={Main.warning}
              buttons={dowJonesBlockedAdminButton}
              textAreaMarginBottom="0"
              titleMarginBottom={Space.spBasePlus}
            />
          </>
        )
    ) : null;

  const updateOptionalInfo = (checked: any) => {
    if (checked) {
      setCheckboxValue(true);
      setOpenOptionalInfoSection(true);
    } else {
      setCheckboxValue(false);
      setOpenOptionalInfoSection(false);
    }
  }

  const consignmentShippingCostApproval = consignmentDetailsResponse.state === AWAITS_SHIPPING_APPROVAL ? (
    <BannerWraper>
      <Banner
        margin='0'
        title='The order is sent to the customer for the shipping cost approval'
        text={`Sent on ${formatDateTime(consignmentDetailsResponse.updated, true)}`}
        text1='You will be notified of the status update by email.'
        textMargin='12px'
        bgColor={Background.white}
        textAreaMarginBottom="0"
        titleMarginBottom={Space.spBasePlus}
        bannerHtml={
          <AdditionalShippingCostApproval
            consignmentDetails={consignmentDetailsResponse}
            onActionComplete={() => consignmentReq.reload()}
          />
        }
      />
    </BannerWraper>
  ) : null

  const consignmentDowJones = consignmentDetailsResponse.state === DOW_JONES_FAILED
    ? (
      isVendor
        ? (
          orderComplianceCheck
        ) : (
          <>
            {
              consignmentDetailsResponse.consignmentType === CONSIGNMENT_TYPES.RENT &&
              <WarningBanner message={TEXT_LABELS.RENT_MEASUREMENT_UNIT_INFO} fontSize='16px' />
            }
            <Banner
              title='The customer has failed the Dow Jones compliance check and requires a review'
              text='Till then this order will stay on hold'
              bgColor={Main.warning}
              buttons={dowJonesBlockedAdminButton}
              textAreaMarginBottom="0"
              titleMarginBottom={Space.spBasePlus}
            />
          </>
        )
    ) : null;

  const splitName = consignmentDetailsResponse.consignmentCount > 1 && (!isVendor && !User.isBrandLevelRoles(user!))
    ? (
      <FlexboxContainer>
        <Link className="base" href={`/orders/split-order-details/${consignmentDetailsResponse.order.orderId}`}>
          <BreadcrumbContainer padding={Space.spBase}>
            Split order # {consignmentDetailsResponse.order.orderId}
          </BreadcrumbContainer>
        </Link>
        <FlexboxContainer padding={`${Space.spBase} 0`} alignItems="center">
          <StyledSlashIcon />
        </FlexboxContainer>
        <BreadcrumbContainer padding={`0 ${Space.spBase}`} alignItems="center">
          {
            isShipping ?
              `Delivery order # ${consignmentDetailsResponse.consignmentId.slice(4)}`
              : `Pickup order # ${consignmentDetailsResponse.consignmentId.slice(4)}`
          }
        </BreadcrumbContainer>
      </FlexboxContainer>
    )
    : (
      <BreadcrumbContainer padding={`0 ${Space.spBase}`} alignItems="center">
        {
          isShipping
            ? `Delivery order # ${consignmentDetailsResponse.consignmentId.slice(4)}`
            : `Pickup order # ${consignmentDetailsResponse.consignmentId.slice(4)}`
        }
      </BreadcrumbContainer>
    );

  // Filter and sort consignment history changes
  const consignmentHistory = collapseSameStatusHistoryRuns(
    (consignmentDetailsResponse?.consignmentHistoryChanges || [])
      .filter((item) => {
        if (item.toConsignmentStatus === NEW) return true;
        else if (isWithdrawOrBuyerDecline(item)) return true;
        else {
          return !HIDE_STATUS_ON_ORDER_HISTORY.includes(item.toConsignmentStatus)
            && (item.fromConsignmentStatus !== item.toConsignmentStatus
              || ALLOW_SAME_STATUS_ON_ORDER_HISTORY.includes(item.toConsignmentStatus))
        }
      }
      ).sort((a, b) => new Date(a.createdOn).getTime() - new Date(b.createdOn).getTime())
  );


  // Fields to compare between current and previous items
  const fieldsToCompareForHistoryChange = [
    'tracking',
    'deliveryDate',
    'carrierName',
    'rentStartDate',
    'rentEndDate'
  ];

  const mappedHistory = mapCurrentAndPreviousValuesInArray(consignmentHistory, fieldsToCompareForHistoryChange).sort((a, b) => new Date(b.createdOn).getTime() - new Date(a.createdOn).getTime());

  const consignmentHistoryView =
    <Banner
      boxShadow='0px 8px 16px rgba(47, 66, 83, 0.25), 0px 16px 20px rgba(47, 66, 83, 0.15)'
      bgColor={Background.white}
    >
      <>
        <FlexboxContainer justifyContent='space-between'>
          {renderOrderDetailsTitle('Order progress')}
          <Button
            onClick={() => printComponent('order-progress-printable-content', `Order History ${parseConsignmentId(consignmentDetailsResponse.consignmentId)}`, 'Order Progress')}
            leftIcon={() => <PrintIcon />}
            background="white"
            borderColor="white"
          />
        </FlexboxContainer>
        <OrderProgress
          consignmentHistoryChanges={mappedHistory || []}
          consignmentModificationEntries={consignmentDetailsResponse?.modificationEntries!}
          consignmentSnapshotEntries={consignmentDetailsResponse?.consignmentSnapshotEntries}
          consignmentSnapshot={consignmentDetailsResponse?.consignmentSnapshot}
          consignmentState={consignmentDetailsResponse?.state}
          featureFlags={featureFlags}
          isSubscription={isSubscription || isAffiliateProgram}
        />
      </>
    </Banner>

  const ticketHistoryView =
    <TicketHistory tickets={consignmentDetailsResponse?.tickets!} consignmentId={consignmentDetailsResponse.consignmentId} deliveryType={consignmentDetailsResponse.deliveryType} />

  let orderAddonsView = <OrderSnapshotPreview
    consignmentDetailsResponse={consignmentDetailsResponse}
  />

  // If the consignment is in the MODIFIED_BY_CUSTOMER state and only dates have changed, don't render the component
  if (!!consignmentDetailsResponse?.modificationEntry?.onlyDatesChanged && [MODIFIED_BY_CUSTOMER, MODIFIED_BY_VENDOR].includes(consignmentDetailsResponse.state)) orderAddonsView = <></>;

  // If the consignment is not in the MODIFIED_BY_CUSTOMER / MODIFIED_BY_VENDOR state, don't render the component
  if (![MODIFIED_BY_CUSTOMER, MODIFIED_BY_VENDOR].includes(consignmentDetailsResponse.state)) orderAddonsView = <></>;

  return (
    <Page>
      {modal}
      {declinePostOrderShippingCostModal}
      {modalOnHold}
      {fulfillmentModal}
      {confirmFulfillmentModal}
      {dispatchModal}
      {manufactureModal}
      {pickupModal}
      {delayModal}
      {delayAcceptModal}
      {confirmOrderModal}
      {!isSubscription && <SalesOrderModal show={salesOrderModalOpen} isLoading={salesOrderLoading} onClose={onModalClose} onConfirm={salesOrderUpdateFn} salesOrderNumber={salesOrderResult[0]?.salesOrder || consignmentDetailsResponse.salesOrderNumber} isError={salesOrderError} isTimeout={salesOrderModalTimeout} saveAnyway={saveSalesOrderNumber} setError={setSalesOrderError} setTimeout={setSalesOrderModalTimeout} />}
      {!isSubscription && <SalesOrderDetailsModal show={salesOrderDetailsModalOpen} salesOrderDetails={salesOrderResult} onClose={onModalClose} />}
      <FlexboxContainer justifyContent='space-between'>
        {!isSubscription && <Breadcrumbs containerPadding="0 0 24px 8px" crumbs={crumbs} splitOrderName={splitName} />}
        {isReviewPage && <Button
          onClick={() => printComponent('order-update-preview-printable-content', `Order Update Preview ${consignmentDetailsResponse.consignmentId}`, 'Order update preview')}
          leftIcon={() => <PrintIcon />}
          background="white"
          borderColor="white"
        />
        }
      </FlexboxContainer>
      <FlexboxContainer justifyContent="space-between" width="100%">
        <FlexboxContainer justifyContent="column" alignItems="center">
          {isShipping ? <StyledLocalShipping /> : <StyledWarehouseIcon />}
          <h3 style={{ fontWeight: FontWeight.medium }}>
            {isSubscription ? 'Subscription order' : isShipping ? 'Delivery order' : 'Pickup order'} &nbsp; # {consignmentDetailsResponse.consignmentId.slice(4)}
          </h3>
          {pill}
        </FlexboxContainer>
        <FlexboxContainer
          justifyContent="flex-end"
          margin='0 20px 0 0'
        >
          {
            !isReviewPage && isAwaitingEditApproval ? (
              // Consignment can no longer be canceled while the buyer's approval is pending
              <Button
                isGhost
                color={Text.primary}
                borderColor={Border.main}
                padding={`0 ${Space.sp2x}`}
                leftIcon={(size: string) => <Icon name="arrow-left" size={parseInt(size)} />}
                disabled={isApprovalRequestPending}
              >
                {TEXT_LABELS.BACK_TO_ORDER_DETAILS}
              </Button>
            ) : (
              !isReviewPage && (
                (consignmentDetailsResponse.consignmentType === CONSIGNMENT_TYPES.PURCHASE && consignmentDetailsResponse.order.customer.dowJonesStatus !== DOW_JONES_BLOCKED) ||
                (consignmentDetailsResponse.consignmentType === CONSIGNMENT_TYPES.RENT && !(hiddenStates.includes(consignmentDetailsResponse.state)))) && (
                <Button
                  borderColor='white'
                  isGhost
                  theme='error'
                  color={Main.error}
                  onClick={handleShowCancellationConsignmentPopup}
                  disabled={disableCancelConsignmentButton()}
                  permissionCode={PermissionCode.CANCEL_CONSIGNMENT}
                >
                  Cancel consignment
                </Button>
              )
            )
          }

        </FlexboxContainer>
      </FlexboxContainer>
      <FlexboxContainer flexDirection='row' gap='10px' margin='0px 20px'>
        <OrderTag type={consignmentDetailsResponse.consignmentType} />
        <PlacedDate>Placed {formatDateTime(consignmentDetailsResponse.consignmentDate, true)} {getLocalTimezone()}</PlacedDate>
      </FlexboxContainer>
      <Divider />
      {isAwaitingEditApproval ? (
        <AwaitingEditApproval consignmentDetails={consignmentDetailsResponse} onActionComplete={() => consignmentReq.reload()} />
      ) : (
        <>
          {orderActions}
          {subscriptionOrderActions}
          {dowJonesBanner}
          {consignmentDowJonesBaner}
          {consignmentShippingCostApproval}
          {consignmentDowJones}
          {!isSubscription &&
            consignmentDetailsResponse.businessUnit?.brand?.brandVendor?.isSalesOrderApiEnabled &&
            <SalesOrderErrorBanner
            salesOrderStatus={consignmentDetailsResponse.salesOrderStatus}
              consignmentState={consignmentDetailsResponse.state}
              onRetry={handleRetrySalesOrderCreation}
              disabled={retrySalesOrderReq.isLoading || consignmentDetailsResponse?.salesOrderStatus?.status === SALES_ORDER_API_STATUSES.IN_PROGRESS}
              isSalesOrderApplicable={isSalesOrderInProgress}
            />
          }
          {orderPreparationBanner}
          {cancellationBanner}
          {incorporatingEditChangesBanner}
          {fulfillmentTable}
          {postOrderShippingCostSplitForm}
          {updates}
          {orderReadyActions}
          {orderSplittingBanner}
          {orderOnHoldAfterCheckingCreditActions}
          {orderCancelingActions}
          {getManufacturingBanner()}
          {awaitingDispatchActions}
          {rentalSteps}
          {subscriptionSteps}
          {affiliateSteps}
          {orderFulfilled}
          {delayedActions}
          {orderInformation}
          {featureFlags?.salesOrderCreation && featureFlags?.salesOrderCreation.enabled && salesOrderComponent}
          {orderAddonsView}
          {mappedHistory.length > 1 && consignmentHistoryView}
          {ticketHistoryView}
          {orderProducts}
          {orderFulfillmentTable}
          {orderPreparation}
        </>
      )}
      {cancellationOrderEmailModal}
      {cancellationConsignmentModal}
      {cancellationApprovalModal}
      {updateOrderInfoModal}
      {modifyRentalDatesModal}
    </Page>
  );
};

export default React.memo(OrderDetails);
